/**
 * ClashSandboxModal - Interactive Multi-Brand Track Preset & Hazard Sandbox
 * Allows players in Cribbage Clash, Pints & Pegs, and Skunked & Stripped to visually inspect,
 * drag-and-drop, reposition, and customize hazards, drinking spaces, and strip/dare challenges
 * around the board before match launch.
 */

import { BOARD_CFG, getClashBoardCfg, getTrackXY } from '../board/BoardGeometry.js';
import { ObstacleGeometry } from '../board/ObstacleGeometry.js';
import { ClashSettings, getClashBlockDefs } from './ClashSettings.js';
import { ClashPresets, CLASH_PRESETS_481 } from './ClashPresets.js';
import { PintsPresets, PINTS_PRESETS_121 } from './PintsPresets.js';
import { AdultPresets, ADULT_PRESETS_121 } from './AdultPresets.js';
import { buildClashItemMetadata } from './ClashPlacementModal.js';
import { PINTS_OPTIONS_DEFS } from './PintsManager.js';
import { ADULT_OPTIONS_DEFS } from './AdultManager.js';
import { LayoutSanitizer, MAX_LAYOUT_JSON_LENGTH } from './LayoutSanitizer.js';
import { sanitizeUserInput, sanitizeHtml } from '../utils/Sanitizer.js';

function escapeHtml(str) {
  if (!str) return '';
  return sanitizeUserInput(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export const BRAND_ITEM_PALETTES = Object.freeze({
  clash: [
    { type: 'jail', name: 'Jail', icon: '🔒', color: '#94a3b8', length: 1, desc: 'Locks peg until rolling a double or serving sentence' },
    { type: 'bar', name: 'The Bar', icon: '🍸', color: '#f59e0b', length: 1, desc: 'Diverts peg for refreshments' },
    { type: 'timeout', name: 'Timeout', icon: '⏳', color: '#8b5cf6', length: 1, desc: 'Lose a turn or pay a penalty point' },
    { type: 'jump', name: 'Jump', icon: '⚡', color: '#3b82f6', length: 1, desc: 'Forward or backward shortcut vector' },
    { type: 'noPassingLanes', name: 'No Passing', icon: '🚫', color: '#ef4444', length: 5, desc: '5-hole bottleneck where passing is blocked' },
    { type: 'stopSign', name: 'Stop Sign', icon: '🛑', color: '#ef4444', length: 1, desc: 'Forces peg movement to halt on this space' },
    { type: 'signs', name: 'Sign Block', icon: '🪧', color: '#ec4899', length: 3, desc: 'Mystery interactive event block' },
    { type: 'detour', name: 'Detour', icon: '🔀', color: '#06b6d4', length: 1, desc: 'Scenic bypass around a stretch of track' },
    { type: 'roundAbout', name: 'Roundabout', icon: '🔄', color: '#10b981', length: 1, desc: 'Circular traffic loop' },
    { type: 'dice', name: 'Dice', icon: '🎲', color: '#a855f7', length: 1, desc: 'Roll the dice minigame space' },
    { type: 'wheel', name: 'Spinning Wheel', icon: '🎡', color: '#ec4899', length: 1, desc: 'Spin the wheel minigame space' }
  ],
  pints: [
    { type: 'youDrink', name: 'You Drink', icon: '🍺', color: '#f59e0b', length: 1, desc: 'Player landing here takes a drink / shot' },
    { type: 'guysDrink', name: 'Guys Drink', icon: '🍻', color: '#6366f1', length: 1, desc: 'All male players at the table take a drink' },
    { type: 'galsDrink', name: 'Gals Drink', icon: '🍷', color: '#f43f5e', length: 1, desc: 'All female players at the table take a drink' },
    { type: 'leftDrink', name: 'Left Drinks', icon: '👈', color: '#3b82f6', length: 1, desc: 'Player to your left takes a drink' },
    { type: 'rightDrink', name: 'Right Drinks', icon: '👉', color: '#06b6d4', length: 1, desc: 'Player to your right takes a drink' },
    { type: 'everyoneDrink', name: 'Everyone Drinks', icon: '🥂', color: '#10b981', length: 1, desc: 'Social! Everyone playing takes a drink' },
    { type: 'everyoneButYouDrink', name: 'Everyone But You', icon: '🎉', color: '#8b5cf6', length: 1, desc: 'Everyone except the pegging player takes a drink' },
    { type: 'pickWhoDrinks', name: 'Pick Who Drinks', icon: '🎯', color: '#ec4899', length: 1, desc: 'Choose any opponent to take a drink' }
  ],
  adult: [
    { type: 'loseClothing', name: 'You Strip', icon: '👙', color: '#f43f5e', length: 1, desc: 'Player loses an article of clothing' },
    { type: 'gainClothing', name: 'Gain Clothing', icon: '🧥', color: '#10b981', length: 1, desc: 'Player regains an article of clothing' },
    { type: 'pickWhoLosesClothing', name: 'Pick Who Strips', icon: '🎯', color: '#ec4899', length: 1, desc: 'Choose who loses an article of clothing' },
    { type: 'rightLosesClothing', name: 'Right Strips', icon: '👉', color: '#3b82f6', length: 1, desc: 'Person to your right loses clothing' },
    { type: 'leftLosesClothing', name: 'Left Strips', icon: '👈', color: '#8b5cf6', length: 1, desc: 'Person to your left loses clothing' },
    { type: 'removeClothingChoice', name: 'Remove Choice', icon: '✂️', color: '#e11d48', length: 1, desc: 'Remove clothing off player of choice' },
    { type: 'touchBreast', name: 'Touch Breast', icon: '💋', color: '#f43f5e', length: 1, desc: 'Timed intimate challenge: Touch breast' },
    { type: 'lickBreast', name: 'Lick Breast', icon: '👅', color: '#ec4899', length: 1, desc: 'Timed intimate challenge: Lick breast' },
    { type: 'suckBreast', name: 'Suck Breast', icon: '👄', color: '#d946ef', length: 1, desc: 'Timed intimate challenge: Suck breast' },
    { type: 'touchGenitals', name: 'Touch Genitals', icon: '✨', color: '#a855f7', length: 1, desc: 'Timed intimate challenge: Touch genitals' },
    { type: 'lickGenitals', name: 'Lick Genitals', icon: '👅', color: '#f43f5e', length: 1, desc: 'Timed intimate challenge: Lick genitals' },
    { type: 'suckGenitals', name: 'Suck Genitals', icon: '👄', color: '#ec4899', length: 1, desc: 'Timed intimate challenge: Suck genitals' },
    { type: 'fingerGenitals', name: 'Finger Genitals', icon: '👉', color: '#f59e0b', length: 1, desc: 'Timed intimate challenge: Finger genitals' }
  ]
});

export class ClashSandboxModal {
  static pickingDestForJumpId = null;
  static draggedDestOfJump = null;
  static draggedTrayType = null;
  static activeSnappedHole = null;
  static isOpen = false;
  static _dragGuardsAttached = false;
  static _cachedHolesTarget = null;
  static _cachedHolesCoords = null;

  static init(brand = 'clash') {
    this.brand = brand;
    this.activeLayout = null;
    this.selectedTrayType = null;
    this.draggedItem = null;
    this.targetVal = brand === 'clash' ? 481 : 121;
    this.activeCustomLayoutId = null;
    this.activeCustomLayoutName = null;
    this.currentExportedLayout = null;
    this.pickingDestForJumpId = null;
    this.draggedDestOfJump = null;
    this.draggedTrayType = null;
    this.activeSnappedHole = null;
    ClashSandboxModal.cleanupFloatingGhosts();
  }

  /**
   * Attaches window-level drag guards while the sandbox modal is open
   * to completely prevent the browser from navigating to dropped text or freezing.
   */
  static attachGlobalDragGuards() {
    if (ClashSandboxModal._dragGuardsAttached || typeof window === 'undefined') return;
    ClashSandboxModal._dragGuardsAttached = true;

    ClashSandboxModal._onGlobalDragOver = (e) => {
      if (ClashSandboxModal.isOpen) {
        e.preventDefault();
      }
    };
    ClashSandboxModal._onGlobalDrop = (e) => {
      if (ClashSandboxModal.isOpen) {
        e.preventDefault();
      }
    };
    window.addEventListener('dragover', ClashSandboxModal._onGlobalDragOver, { passive: false });
    window.addEventListener('drop', ClashSandboxModal._onGlobalDrop, { passive: false });
  }

  static removeGlobalDragGuards() {
    if (!ClashSandboxModal._dragGuardsAttached || typeof window === 'undefined') return;
    ClashSandboxModal._dragGuardsAttached = false;
    if (ClashSandboxModal._onGlobalDragOver) {
      window.removeEventListener('dragover', ClashSandboxModal._onGlobalDragOver);
    }
    if (ClashSandboxModal._onGlobalDrop) {
      window.removeEventListener('drop', ClashSandboxModal._onGlobalDrop);
    }
  }

  static cleanupFloatingGhosts() {
    if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') return;
    document.querySelectorAll('.sb-floating-drag-ghost').forEach(el => el.remove());
  }

  /**
   * Returns precomputed { x, y } coordinates for every hole up to targetVal - 1.
   */
  static getHoleCoordinatesMap(targetVal) {
    if (ClashSandboxModal._cachedHolesTarget === targetVal && ClashSandboxModal._cachedHolesCoords) {
      return ClashSandboxModal._cachedHolesCoords;
    }
    const totalHoles = targetVal - 1;
    const coords = new Array(totalHoles + 1);
    for (let h = 1; h <= totalHoles; h++) {
      coords[h] = getTrackXY(h, 'p1', targetVal);
    }
    ClashSandboxModal._cachedHolesTarget = targetVal;
    ClashSandboxModal._cachedHolesCoords = coords;
    return coords;
  }

  /**
   * Translates client pointer coordinates into the board SVG viewBox coordinate space.
   */
  static getSvgCoords(e, svg) {
    if (!svg) return { x: 0, y: 0 };
    const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);

    const pt = svg.createSVGPoint ? svg.createSVGPoint() : null;
    const ctm = svg.getScreenCTM ? svg.getScreenCTM() : null;
    if (pt && ctm) {
      pt.x = clientX;
      pt.y = clientY;
      return pt.matrixTransform(ctm.inverse());
    }
    const rect = svg.getBoundingClientRect();
    const vb = svg.viewBox && svg.viewBox.baseVal ? svg.viewBox.baseVal : { width: 820, height: 438 };
    const scaleX = vb.width / (rect.width || 1);
    const scaleY = vb.height / (rect.height || 1);
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }

  /**
   * Snaps any coordinate on the board to the closest hole number (1 to targetVal - 1).
   */
  static findClosestHole(svgX, svgY, targetVal) {
    const coords = ClashSandboxModal.getHoleCoordinatesMap(targetVal);
    let closestHole = 1;
    let minDistSq = Infinity;
    for (let h = 1; h < coords.length; h++) {
      const pt = coords[h];
      if (!pt) continue;
      const dx = pt.x - svgX;
      const dy = pt.y - svgY;
      const dSq = dx * dx + dy * dy;
      if (dSq < minDistSq) {
        minDistSq = dSq;
        closestHole = h;
      }
    }
    return closestHole;
  }

  /**
   * Highlights the nearest snapped hole with visual pulse and moves the indicator ring.
   */
  static updateSnapHighlight(hole, svg) {
    if (!hole || !svg) return;
    if (ClashSandboxModal.activeSnappedHole === hole) return;

    if (ClashSandboxModal.activeSnappedHole) {
      const prevEl = svg.querySelector(`.sb-hole[data-hole="${ClashSandboxModal.activeSnappedHole}"]`);
      if (prevEl) prevEl.classList.remove('sb-hole-snap');
    }

    ClashSandboxModal.activeSnappedHole = hole;
    const curEl = svg.querySelector(`.sb-hole[data-hole="${hole}"]`);
    if (curEl) curEl.classList.add('sb-hole-snap');

    const snapIndicator = svg.querySelector('#sbSnapIndicator');
    if (snapIndicator) {
      const pt = getTrackXY(hole, 'p1', ClashSandboxModal.targetVal);
      snapIndicator.setAttribute('cx', pt.x);
      snapIndicator.setAttribute('cy', pt.y);
      snapIndicator.setAttribute('opacity', '1');
    }
  }

  /**
   * Clears any active snapped hole highlight.
   */
  static clearSnapHighlight(svg) {
    if (!svg && typeof document !== 'undefined') svg = document.querySelector('.sandbox-svg-board');
    if (ClashSandboxModal.activeSnappedHole && svg) {
      const prevEl = svg.querySelector(`.sb-hole[data-hole="${ClashSandboxModal.activeSnappedHole}"]`);
      if (prevEl) prevEl.classList.remove('sb-hole-snap');
    }
    ClashSandboxModal.activeSnappedHole = null;
    if (svg) {
      const snapIndicator = svg.querySelector('#sbSnapIndicator');
      if (snapIndicator) snapIndicator.setAttribute('opacity', '0');
    }
  }

  /**
   * Retrieves the block definitions for Clash from ClashSettings.getBlockDefs().
   * @returns {Array<Object>}
   */
  static getClashBlockDefs() {
    try {
      if (typeof getClashBlockDefs === 'function') {
        const defs = getClashBlockDefs();
        if (Array.isArray(defs) && defs.length) return defs;
      }
      if (typeof ClashSettings !== 'undefined' && typeof ClashSettings.getBlockDefs === 'function') {
        return ClashSettings.getBlockDefs();
      }
      if (typeof window !== 'undefined' && window.ClashSettings && typeof window.ClashSettings.getBlockDefs === 'function') {
        return window.ClashSettings.getBlockDefs();
      }
      if (typeof window !== 'undefined' && window.CC?.ClashSettings?.getBlockDefs) {
        return window.CC.ClashSettings.getBlockDefs();
      }
      if (typeof window !== 'undefined' && window.crazyCribbageApp?.ClashSettings?.getBlockDefs) {
        return window.crazyCribbageApp.ClashSettings.getBlockDefs();
      }
    } catch (_) {}
    return [];
  }

  /**
   * Finds the block definition for a specific hazard/item type in ClashSettings.
   * @param {string} type
   * @returns {Object|null}
   */
  static getClashBlockDef(type) {
    const defs = ClashSandboxModal.getClashBlockDefs();
    return defs.find(d => d.type === type || d.key === type) || null;
  }

  /**
   * Retrieves the maximum number allowed for an item type.
   * For Cribbage Clash, this comes directly from ClashSettings.getBlockDefs().
   * @param {string} type
   * @param {string} [brand=ClashSandboxModal.brand]
   * @returns {number}
   */
  static getItemMaxCount(type, brand = ClashSandboxModal.brand) {
    const resolvedBrand = brand || ClashSandboxModal.brand || 'clash';
    if (resolvedBrand === 'clash') {
      const def = ClashSandboxModal.getClashBlockDef(type);
      if (def && typeof def.maxCount === 'number') {
        return def.maxCount;
      }
      const clashFallbackMaxes = {
        noPassingLanes: 4,
        jump: 8,
        jail: 4,
        bar: 4,
        timeout: 4,
        detour: 4,
        roundAbout: 4,
        signs: 8,
        stopSign: 8,
        dice: 4,
        wheel: 4
      };
      if (clashFallbackMaxes[type] !== undefined) return clashFallbackMaxes[type];
    }
    if (resolvedBrand === 'pints') return 4;
    if (resolvedBrand === 'adult') return 4;
    return 4;
  }

  /**
   * Retrieves the element ID of the count dropdown for a given hazard/item type.
   * Uses countId from ClashSettings.getBlockDefs() for clash items.
   * @param {string} type
   * @returns {string}
   */
  static getCountElementId(type) {
    const clashDef = ClashSandboxModal.getClashBlockDef(type);
    if (clashDef && clashDef.countId) return clashDef.countId;
    if (type === 'noPassingLanes') return 'optNoPassingLaneCount';
    if (type === 'wheel') {
      return (typeof document !== 'undefined' && document.getElementById('optSpinningWheelCount')) ? 'optSpinningWheelCount' : 'optWheelCount';
    }
    return `opt${type.charAt(0).toUpperCase() + type.slice(1)}Count`;
  }

  /**
   * Retrieves the maximum allowed count for an item type in the sandbox.
   * For Cribbage Clash, this comes directly from ClashSettings.getBlockDefs().
   * @param {string} type
   * @param {string} [brand=ClashSandboxModal.brand]
   * @param {Object} [settings=null]
   * @returns {number}
   */
  static getItemLimit(type, brand = ClashSandboxModal.brand, settings = null) {
    const resolvedBrand = brand || ClashSandboxModal.brand || 'clash';
    return ClashSandboxModal.getItemMaxCount(type, resolvedBrand);
  }

  static _onExternalCountChange = () => {
    if (!ClashSandboxModal.isOpen) return;
    ClashSandboxModal.refreshView();
  };

  static bindExternalCountListeners() {
    if (typeof document === 'undefined') return;
    const countEls = document.querySelectorAll('select[id$="Count"]');
    countEls.forEach(el => {
      el.removeEventListener('change', ClashSandboxModal._onExternalCountChange);
      el.addEventListener('change', ClashSandboxModal._onExternalCountChange);
    });
  }

  static unbindExternalCountListeners() {
    if (typeof document === 'undefined') return;
    const countEls = document.querySelectorAll('select[id$="Count"]');
    countEls.forEach(el => {
      el.removeEventListener('change', ClashSandboxModal._onExternalCountChange);
    });
  }

  /**
   * Retrieves the preset & storage engine corresponding to the current brand.
   * @param {string} [brand=ClashSandboxModal.brand]
   * @returns {Object}
   */
  static getPresetEngine(brand = ClashSandboxModal.brand) {
    if (brand === 'pints') return PintsPresets;
    if (brand === 'adult') return AdultPresets;
    return ClashPresets;
  }

  /**
   * Opens the interactive Hazard Creator Sandbox for Clash, Pints & Pegs, or Skunked & Stripped.
   * @param {Object} [context={}]
   */
  static open(context = {}) {
    if (typeof document === 'undefined') return;

    try {
      const detectedBrand = context.brand || (typeof window !== 'undefined' && window.getAppBrand ? window.getAppBrand() : (window.currentBrand || 'clash'));
      const brand = ['pints', 'adult', 'clash'].includes(detectedBrand) ? detectedBrand : 'clash';
      ClashSandboxModal.init(brand);
      ClashSandboxModal.isOpen = true;
      ClashSandboxModal.attachGlobalDragGuards();

      const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
      const fallbackTarget = brand === 'clash' ? 481 : 121;
      const targetVal = Number(settings.target || fallbackTarget);
      ClashSandboxModal.targetVal = [121, 241, 361, 481].includes(targetVal) ? targetVal : fallbackTarget;

      const customPresetProp = brand === 'pints' ? 'pintsPreset' : (brand === 'adult' ? 'adultPreset' : 'clashPreset');
      const customLayoutIdProp = brand === 'pints' ? 'pintsCustomLayoutId' : (brand === 'adult' ? 'adultCustomLayoutId' : 'clashCustomLayoutId');
      const customLayoutNameProp = brand === 'pints' ? 'pintsCustomLayoutName' : (brand === 'adult' ? 'adultCustomLayoutName' : 'clashCustomLayoutName');

      if (settings[customPresetProp] === 'custom' && settings[customLayoutIdProp]) {
        ClashSandboxModal.activeCustomLayoutId = settings[customLayoutIdProp];
        ClashSandboxModal.activeCustomLayoutName = settings[customLayoutNameProp] || 'Custom Layout';
      }

      // Load active layout from current settings / form or fallback to standard preset
      ClashSandboxModal.activeLayout = ClashSandboxModal.loadLayoutFromSettings(settings, brand);

      // Enforce dropdown count limits defined in Board Layout Options
      const counts = {};
      ClashSandboxModal.activeLayout = (ClashSandboxModal.activeLayout || []).filter(item => {
        const limit = ClashSandboxModal.getItemLimit(item.type, brand, settings);
        const curr = counts[item.type] || 0;
        if (curr < limit) {
          counts[item.type] = curr + 1;
          return true;
        }
        return false;
      });

      ClashSandboxModal.bindExternalCountListeners();

      let overlay = document.getElementById('clashSandboxOverlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'clashSandboxOverlay';
        overlay.className = 'clash-sandbox-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', 'Track Hazard Sandbox');
        document.body.appendChild(overlay);
      }

      overlay.style.display = 'flex';
      ClashSandboxModal.renderModal(overlay);

      const btnVariantsMenu = document.getElementById('btnVariantsMenu');
      if (btnVariantsMenu) {
        btnVariantsMenu.classList.add('is-active');
        const b = brand || 'clash';
        const label = b === 'crazy' ? 'Crazy Variants' : 'Board Layout';
        btnVariantsMenu.title = `Hide ${label}`;
        btnVariantsMenu.setAttribute('aria-label', `Hide ${label}`);
      }

      if (context.openDrawer) {
        setTimeout(() => ClashSandboxModal.openLayoutsDrawer(), 60);
      }
    } catch (err) {
      console.error('[ClashSandboxModal] Error opening sandbox:', err);
    }
  }

  /**
   * Closes the Hazard Creator Sandbox without saving changes.
   */
  static close() {
    if (typeof document === 'undefined') return;
    ClashSandboxModal.isOpen = false;
    ClashSandboxModal.removeGlobalDragGuards();
    ClashSandboxModal.cleanupFloatingGhosts();
    ClashSandboxModal.unbindExternalCountListeners();

    const overlay = document.getElementById('clashSandboxOverlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
    const btnVariantsMenu = document.getElementById('btnVariantsMenu');
    if (btnVariantsMenu) {
      btnVariantsMenu.classList.remove('is-active');
      const b = ClashSandboxModal.brand || 'clash';
      const label = b === 'crazy' ? 'Crazy Variants' : 'Board Layout';
      btnVariantsMenu.title = label;
      btnVariantsMenu.setAttribute('aria-label', label);
    }
    ClashSandboxModal.draggedItem = null;
    ClashSandboxModal.draggedDestOfJump = null;
    ClashSandboxModal.draggedTrayType = null;
    ClashSandboxModal.activeSnappedHole = null;
    ClashSandboxModal.pickingDestForJumpId = null;
    ClashSandboxModal.selectedTrayType = null;
    const svgWrap = document.getElementById('sandboxSvgWrap');
    if (svgWrap) svgWrap.classList.remove('sandbox-picking-active');
  }

  /**
   * Extracts placed spaces/hazards from active settings into an array of editable items.
   * @param {Object} settings
   * @param {string} [brand=ClashSandboxModal.brand]
   * @returns {Array<Object>}
   */
  static loadLayoutFromSettings(settings, brand = ClashSandboxModal.brand) {
    const items = [];
    const getVal = (id, arrKey = null, idx = 0) => {
      const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
      if (el && el.value !== '' && !isNaN(parseInt(el.value, 10))) return parseInt(el.value, 10);
      if (typeof settings?.[id] === 'number') return settings[id];
      if (typeof settings?.[id] === 'string' && settings[id].trim() !== '' && !isNaN(parseInt(settings[id], 10))) {
        return parseInt(settings[id], 10);
      }
      if (arrKey && Array.isArray(settings?.[arrKey]) && typeof settings[arrKey][idx] === 'number') {
        return settings[arrKey][idx];
      }
      return 0;
    };
    const isChecked = (chkId, cntId, idx, arrKey = null, type = null) => {
      const limit = type ? ClashSandboxModal.getItemLimit(type, brand, settings) : null;
      let cnt = limit !== null ? limit : null;
      if (cnt === null) {
        const el = typeof document !== 'undefined' ? document.getElementById(cntId) : null;
        if (el && el.value !== '' && !isNaN(parseInt(el.value, 10))) {
          cnt = parseInt(el.value, 10);
        } else if (typeof settings?.[cntId] === 'number') {
          cnt = settings[cntId];
        } else if (typeof settings?.[cntId] === 'string' && !isNaN(parseInt(settings[cntId], 10))) {
          cnt = parseInt(settings[cntId], 10);
        } else if (arrKey && Array.isArray(settings?.[arrKey])) {
          cnt = settings[arrKey].length;
        } else {
          cnt = 1;
        }
      }
      if (idx > cnt) return false;

      const chk = typeof document !== 'undefined' ? document.getElementById(chkId) : null;
      const chkVal = chk ? chk.checked : (settings?.[chkId] !== undefined ? Boolean(settings[chkId]) : true);
      return Boolean(chkVal);
    };

    if (brand === 'pints') {
      const palette = BRAND_ITEM_PALETTES.pints;
      palette.forEach(opt => {
        const chkId = `opt${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}`;
        const cntId = ClashSandboxModal.getCountElementId(opt.type);
        const arrKey = `${opt.type}Holes`;
        for (let i = 1; i <= 4; i++) {
          if (isChecked(chkId, cntId, i, arrKey, opt.type)) {
            const h = getVal(`opt${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}${i}Hole`, arrKey, i - 1);
            if (h > 0) {
              items.push({
                id: `${opt.type}_${i}`,
                type: opt.type,
                name: `${opt.name} ${i}`,
                icon: opt.icon,
                color: opt.color,
                hole: h,
                length: 1
              });
            }
          }
        }
      });

      if (!items.length) {
        return ClashSandboxModal.convertPresetToItems(PintsPresets.getPresets(ClashSandboxModal.targetVal).standard.config, 'pints');
      }
      return items;
    }

    if (brand === 'adult') {
      const palette = BRAND_ITEM_PALETTES.adult;
      palette.forEach(opt => {
        const chkId = `opt${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}`;
        const cntId = ClashSandboxModal.getCountElementId(opt.type);
        const arrKey = `${opt.type}Holes`;
        for (let i = 1; i <= 4; i++) {
          if (isChecked(chkId, cntId, i, arrKey, opt.type)) {
            const h = getVal(`opt${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}${i}Hole`, arrKey, i - 1);
            if (h > 0) {
              items.push({
                id: `${opt.type}_${i}`,
                type: opt.type,
                name: `${opt.name} ${i}`,
                icon: opt.icon,
                color: opt.color,
                hole: h,
                length: 1
              });
            }
          }
        }
      });

      if (!items.length) {
        return ClashSandboxModal.convertPresetToItems(AdultPresets.getPresets(ClashSandboxModal.targetVal).standard.config, 'adult');
      }
      return items;
    }

    // Default: Cribbage Clash
    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('noPassingLanes', 'clash'); i++) {
      if (isChecked('optNoPassingLanes', 'optNoPassingLaneCount', i, 'noPassingLaneStarts', 'noPassingLanes')) {
        const h = getVal(`optNoPassingLane${i}Start`, 'noPassingLaneStarts', i - 1);
        if (h > 0) items.push({ id: `lane_${i}`, type: 'noPassingLanes', name: `No Passing ${i}`, icon: '🚫', color: '#ef4444', hole: h, length: 5 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('jump', 'clash'); i++) {
      if (isChecked('optJump', 'optJumpCount', i, 'jumpStarts', 'jump')) {
        const s = getVal(`optJump${i}Start`, 'jumpStarts', i - 1);
        const d = getVal(`optJump${i}Dest`, 'jumpDests', i - 1);
        if (s > 0) {
          items.push({
            id: `jump_${i}`,
            type: 'jump',
            name: `Jump ${i}`,
            icon: '⚡',
            color: '#3b82f6',
            hole: s,
            dest: d > 0 ? d : Math.min(ClashSandboxModal.targetVal - 5, s + 30),
            length: 1
          });
        }
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('jail', 'clash'); i++) {
      if (isChecked('optJail', 'optJailCount', i, 'jailHoles', 'jail')) {
        const h = getVal(`optJail${i}Hole`, 'jailHoles', i - 1);
        if (h > 0) items.push({ id: `jail_${i}`, type: 'jail', name: `Jail ${i}`, icon: '🔒', color: '#94a3b8', hole: h, length: 1 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('bar', 'clash'); i++) {
      if (isChecked('optBar', 'optBarCount', i, 'barHoles', 'bar')) {
        const h = getVal(`optBar${i}Hole`, 'barHoles', i - 1);
        if (h > 0) items.push({ id: `bar_${i}`, type: 'bar', name: `The Bar ${i}`, icon: '🍸', color: '#f59e0b', hole: h, length: 1 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('timeout', 'clash'); i++) {
      if (isChecked('optTimeout', 'optTimeoutCount', i, 'timeoutHoles', 'timeout')) {
        const h = getVal(`optTimeout${i}Hole`, 'timeoutHoles', i - 1);
        if (h > 0) items.push({ id: `timeout_${i}`, type: 'timeout', name: `Timeout ${i}`, icon: '⏳', color: '#8b5cf6', hole: h, length: 1 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('detour', 'clash'); i++) {
      if (isChecked('optDetour', 'optDetourCount', i, 'detourHoles', 'detour')) {
        const h = getVal(`optDetour${i}Hole`, 'detourHoles', i - 1);
        if (h > 0) items.push({ id: `detour_${i}`, type: 'detour', name: `Detour ${i}`, icon: '🔀', color: '#06b6d4', hole: h, length: 1 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('roundAbout', 'clash'); i++) {
      if (isChecked('optRoundAbout', 'optRoundAboutCount', i, 'roundAboutHoles', 'roundAbout')) {
        const h = getVal(`optRoundAbout${i}Hole`, 'roundAboutHoles', i - 1);
        if (h > 0) items.push({ id: `roundAbout_${i}`, type: 'roundAbout', name: `Round About ${i}`, icon: '🔄', color: '#10b981', hole: h, length: 1 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('signs', 'clash'); i++) {
      if (isChecked('optSigns', 'optSignsCount', i, 'signsStarts', 'signs')) {
        const h = getVal(`optSigns${i}Hole`, 'signsStarts', i - 1);
        if (h > 0) items.push({ id: `signs_${i}`, type: 'signs', name: `Signs ${i}`, icon: '🪧', color: '#ec4899', hole: h, length: 3 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('stopSign', 'clash'); i++) {
      if (isChecked('optStopSign', 'optStopSignCount', i, 'stopSignHoles', 'stopSign')) {
        const h = getVal(`optStopSign${i}Hole`, 'stopSignHoles', i - 1);
        if (h > 0) items.push({ id: `stopSign_${i}`, type: 'stopSign', name: `Stop Sign ${i}`, icon: '🛑', color: '#ef4444', hole: h, length: 1 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('dice', 'clash'); i++) {
      if (isChecked('optDice', 'optDiceCount', i, 'diceHoles', 'dice')) {
        const h = getVal(`optDice${i}Hole`, 'diceHoles', i - 1);
        if (h > 0) items.push({ id: `dice_${i}`, type: 'dice', name: `Dice ${i}`, icon: '🎲', color: '#a855f7', hole: h, length: 1 });
      }
    }

    for (let i = 1; i <= ClashSandboxModal.getItemMaxCount('wheel', 'clash'); i++) {
      if (isChecked('optWheel', 'optWheelCount', i, 'wheelHoles', 'wheel') || isChecked('optSpinningWheel', 'optWheelCount', i, 'wheelHoles', 'wheel') || isChecked('optSpinningWheel', 'optSpinningWheelCount', i, 'wheelHoles', 'wheel')) {
        const h = getVal(`optWheel${i}Hole`, 'wheelHoles', i - 1) || getVal(`optSpinningWheel${i}Hole`, 'spinningWheelHoles', i - 1);
        if (h > 0) items.push({ id: `wheel_${i}`, type: 'wheel', name: `Spinning Wheel ${i}`, icon: '🎡', color: '#ec4899', hole: h, length: 1 });
      }
    }

    if (!items.length) {
      return ClashSandboxModal.convertPresetToItems(CLASH_PRESETS_481.standard.config, 'clash');
    }

    return items;
  }

  /**
   * Converts a preset config object to an items array.
   * @param {Object} cfg
   * @param {string} [brand=ClashSandboxModal.brand]
   * @returns {Array<Object>}
   */
  static convertPresetToItems(cfg, brand = ClashSandboxModal.brand) {
    const items = [];
    if (!cfg) return items;

    if (brand === 'pints') {
      const palette = BRAND_ITEM_PALETTES.pints;
      palette.forEach(opt => {
        const chkKey = `opt${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}`;
        const arr = cfg[`${opt.type}Holes`] || [];
        if (cfg[chkKey] || arr.length > 0) {
          const limit = ClashSandboxModal.getItemLimit(opt.type, brand, cfg);
          const rawCnt = cfg[`${chkKey}Count`] || cfg[`${opt.type}Count`] || arr.length || 2;
          const cnt = Math.min(limit, rawCnt);
          for (let i = 1; i <= cnt; i++) {
            const h = cfg[`${chkKey}${i}Hole`] || arr[i - 1];
            if (h) items.push({ id: `${opt.type}_${i}`, type: opt.type, name: `${opt.name} ${i}`, icon: opt.icon, color: opt.color, hole: h, length: 1 });
          }
        }
      });
      return items;
    }

    if (brand === 'adult') {
      const palette = BRAND_ITEM_PALETTES.adult;
      palette.forEach(opt => {
        const chkKey = `opt${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}`;
        const arr = cfg[`${opt.type}Holes`] || [];
        if (cfg[chkKey] || arr.length > 0) {
          const limit = ClashSandboxModal.getItemLimit(opt.type, brand, cfg);
          const rawCnt = cfg[`${chkKey}Count`] || cfg[`${opt.type}Count`] || arr.length || 2;
          const cnt = Math.min(limit, rawCnt);
          for (let i = 1; i <= cnt; i++) {
            const h = cfg[`${chkKey}${i}Hole`] || arr[i - 1];
            if (h) items.push({ id: `${opt.type}_${i}`, type: opt.type, name: `${opt.name} ${i}`, icon: opt.icon, color: opt.color, hole: h, length: 1 });
          }
        }
      });
      return items;
    }

    // Default: Clash
    if (cfg.optNoPassingLanes || (Array.isArray(cfg.noPassingLaneStarts) && cfg.noPassingLaneStarts.length)) {
      const arr = cfg.noPassingLaneStarts || [];
      const limit = ClashSandboxModal.getItemLimit('noPassingLanes', 'clash', cfg);
      const rawCnt = cfg.optNoPassingLaneCount || cfg.noPassingLaneCount || arr.length || 4;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optNoPassingLane${i}Start`] || arr[i - 1];
        if (h) items.push({ id: `lane_${i}`, type: 'noPassingLanes', name: `No Passing ${i}`, icon: '🚫', color: '#ef4444', hole: h, length: 5 });
      }
    }
    if (cfg.optJump || (Array.isArray(cfg.jumpStarts) && cfg.jumpStarts.length)) {
      const sArr = cfg.jumpStarts || [];
      const dArr = cfg.jumpDests || [];
      const limit = ClashSandboxModal.getItemLimit('jump', 'clash', cfg);
      const rawCnt = cfg.optJumpCount || cfg.jumpCount || sArr.length || 8;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const s = cfg[`optJump${i}Start`] || sArr[i - 1];
        const d = cfg[`optJump${i}Dest`] || dArr[i - 1];
        if (s) items.push({ id: `jump_${i}`, type: 'jump', name: `Jump ${i}`, icon: '⚡', color: '#3b82f6', hole: s, dest: d, length: 1 });
      }
    }
    if (cfg.optJail || (Array.isArray(cfg.jailHoles) && cfg.jailHoles.length)) {
      const arr = cfg.jailHoles || [];
      const limit = ClashSandboxModal.getItemLimit('jail', 'clash', cfg);
      const rawCnt = cfg.optJailCount || cfg.jailCount || arr.length || 2;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optJail${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `jail_${i}`, type: 'jail', name: `Jail ${i}`, icon: '🔒', color: '#94a3b8', hole: h, length: 1 });
      }
    }
    if (cfg.optBar || (Array.isArray(cfg.barHoles) && cfg.barHoles.length)) {
      const arr = cfg.barHoles || [];
      const limit = ClashSandboxModal.getItemLimit('bar', 'clash', cfg);
      const rawCnt = cfg.optBarCount || cfg.barCount || arr.length || 2;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optBar${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `bar_${i}`, type: 'bar', name: `The Bar ${i}`, icon: '🍸', color: '#f59e0b', hole: h, length: 1 });
      }
    }
    if (cfg.optTimeout || (Array.isArray(cfg.timeoutHoles) && cfg.timeoutHoles.length)) {
      const arr = cfg.timeoutHoles || [];
      const limit = ClashSandboxModal.getItemLimit('timeout', 'clash', cfg);
      const rawCnt = cfg.optTimeoutCount || cfg.timeoutCount || arr.length || 2;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optTimeout${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `timeout_${i}`, type: 'timeout', name: `Timeout ${i}`, icon: '⏳', color: '#8b5cf6', hole: h, length: 1 });
      }
    }
    if (cfg.optDetour || (Array.isArray(cfg.detourHoles) && cfg.detourHoles.length)) {
      const arr = cfg.detourHoles || [];
      const limit = ClashSandboxModal.getItemLimit('detour', 'clash', cfg);
      const rawCnt = cfg.optDetourCount || cfg.detourCount || arr.length || 2;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optDetour${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `detour_${i}`, type: 'detour', name: `Detour ${i}`, icon: '🔀', color: '#06b6d4', hole: h, length: 1 });
      }
    }
    if (cfg.optRoundAbout || (Array.isArray(cfg.roundAboutHoles) && cfg.roundAboutHoles.length)) {
      const arr = cfg.roundAboutHoles || [];
      const limit = ClashSandboxModal.getItemLimit('roundAbout', 'clash', cfg);
      const rawCnt = cfg.optRoundAboutCount || cfg.roundAboutCount || arr.length || 2;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optRoundAbout${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `roundAbout_${i}`, type: 'roundAbout', name: `Round About ${i}`, icon: '🔄', color: '#10b981', hole: h, length: 1 });
      }
    }
    if (cfg.optSigns || (Array.isArray(cfg.signsStarts) && cfg.signsStarts.length)) {
      const arr = cfg.signsStarts || [];
      const limit = ClashSandboxModal.getItemLimit('signs', 'clash', cfg);
      const rawCnt = cfg.optSignsCount || cfg.signsCount || arr.length || 4;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optSigns${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `signs_${i}`, type: 'signs', name: `Signs ${i}`, icon: '🪧', color: '#ec4899', hole: h, length: 3 });
      }
    }
    if (cfg.optStopSign || (Array.isArray(cfg.stopSignHoles) && cfg.stopSignHoles.length)) {
      const arr = cfg.stopSignHoles || [];
      const limit = ClashSandboxModal.getItemLimit('stopSign', 'clash', cfg);
      const rawCnt = cfg.optStopSignCount || cfg.stopSignCount || arr.length || 8;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optStopSign${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `stopSign_${i}`, type: 'stopSign', name: `Stop Sign ${i}`, icon: '🛑', color: '#ef4444', hole: h, length: 1 });
      }
    }
    if (cfg.optDice || (Array.isArray(cfg.diceHoles) && cfg.diceHoles.length)) {
      const arr = cfg.diceHoles || [];
      const limit = ClashSandboxModal.getItemLimit('dice', 'clash', cfg);
      const rawCnt = cfg.optDiceCount || cfg.diceCount || arr.length || 4;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optDice${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `dice_${i}`, type: 'dice', name: `Dice ${i}`, icon: '🎲', color: '#a855f7', hole: h, length: 1 });
      }
    }
    if (cfg.optWheel || cfg.optSpinningWheel || (Array.isArray(cfg.wheelHoles) && cfg.wheelHoles.length)) {
      const arr = cfg.wheelHoles || cfg.spinningWheelHoles || [];
      const limit = ClashSandboxModal.getItemLimit('wheel', 'clash', cfg);
      const rawCnt = cfg.optWheelCount || cfg.optSpinningWheelCount || cfg.wheelCount || arr.length || 4;
      const cnt = Math.min(limit, rawCnt);
      for (let i = 1; i <= cnt; i++) {
        const h = cfg[`optWheel${i}Hole`] || cfg[`optSpinningWheel${i}Hole`] || cfg[`wheel${i}Hole`] || arr[i - 1];
        if (h) items.push({ id: `wheel_${i}`, type: 'wheel', name: `Spinning Wheel ${i}`, icon: '🎡', color: '#ec4899', hole: h, length: 1 });
      }
    }
    return items;
  }

  /**
   * Renders the complete Sandbox modal HTML and wires interactive events.
   * @param {HTMLElement} overlay
   */
  static renderModal(overlay) {
    const brand = ClashSandboxModal.brand || 'clash';
    const targetVal = ClashSandboxModal.targetVal;
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const customLayouts = engine.getCustomLayouts ? engine.getCustomLayouts() : [];
    const customCount = customLayouts.length;
    const activeName = ClashSandboxModal.activeCustomLayoutName;

    let modalTitle = 'CRIBBAGE CLASH HAZARD CREATOR';
    let modalIcon = '🛠️';
    let modalSub = `Drag hazards from the tray to any hole, or move placed items around the ${targetVal}-hole circular track.`;
    let presetsHtml = '';

    if (brand === 'pints') {
      modalTitle = 'PINTS & PEGS DRINKING BOARD SANDBOX';
      modalIcon = '🍻';
      modalSub = `Drag drinking tiles to any hole on the ${targetVal === 121 ? '121-hole board' : `${targetVal}-hole track`} to create your custom drinking party layout.`;
      presetsHtml = `
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="standard">🍻 Pub Crawl</button>
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="powerHour">⚡ Power Hour</button>
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="socialHour">🥂 Social Hour</button>
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="battleOfSexes">🥊 Battle of Sexes</button>
      `;
    } else if (brand === 'adult') {
      modalTitle = 'SKUNKED & STRIPPED DARE SANDBOX';
      modalIcon = '💋';
      modalSub = `Place strip and timed challenge spaces along the ${targetVal === 121 ? '121-hole board' : `${targetVal}-hole track`} to craft your intimate game.`;
      presetsHtml = `
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="standard">👗 Warm-Up Strip</button>
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="highHeat">🔥 High Heat</button>
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="sensualGauntlet">⏳ Sensual Gauntlet</button>
      `;
    } else {
      presetsHtml = `
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="standard">🏛️ Classic</button>
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="gauntlet">⚔️ The Gauntlet</button>
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="speedway">🚀 Speedway</button>
        <button type="button" class="sandbox-preset-pill" data-action="apply-preset" data-preset="bottleneck">🛑 Bottleneck</button>
      `;
    }

    overlay.innerHTML = `
      <div class="clash-sandbox-box sandbox-theme-${brand}" id="clashSandboxBox">
        <!-- Top Header -->
        <div class="sandbox-header">
          <div class="sandbox-title-wrap">
            <span class="sandbox-title-icon">${modalIcon}</span>
            <div>
              <div class="sandbox-title-row">
                <h2 class="sandbox-title">${modalTitle}</h2>
                <span class="sandbox-active-badge ${activeName ? 'is-visible' : ''}" id="sandboxActiveBadge">
                  ${activeName ? `📁 Custom: <strong>${escapeHtml(activeName)}</strong>` : ''}
                </span>
              </div>
              <div class="sandbox-sub">${modalSub}</div>
            </div>
          </div>
          <button type="button" class="btn-ghost sandbox-btn-close" data-action="close" title="Cancel and Close">✕ Close</button>
        </div>

        <!-- Presets Quick Bar -->
        <div class="sandbox-presets-bar">
          <div class="sandbox-presets-left">
            <span class="sandbox-presets-label">Layout Presets:</span>
            <div class="sandbox-preset-pills">
              ${presetsHtml}
              <button type="button" class="sandbox-preset-pill sandbox-rand-pill" data-action="randomize">🎲 Full Random</button>
              <button type="button" class="sandbox-preset-pill sandbox-clear-pill" data-action="clear-all">🧹 Clear All</button>
            </div>
          </div>
          <div class="sandbox-presets-right">
            <button type="button" class="sandbox-preset-pill sandbox-custom-pill" id="btnOpenCustomLayouts" data-action="open-drawer" title="View and load saved custom track layouts">
              📁 My Layouts <span class="sandbox-badge-count" id="sandboxCustomLayoutCount">${customCount}</span>
            </button>
            <button type="button" class="sandbox-preset-pill sandbox-save-pill" data-action="open-save" title="Save current hazard placements to your custom layouts library">
              💾 ${activeName ? 'Save / Update' : 'Save Layout'}
            </button>
          </div>
        </div>

        <!-- Live Track Analysis Stats -->
        <div class="sandbox-metrics-bar" id="sandboxMetricsBar">
          ${ClashSandboxModal.renderMetricsBarHtml()}
        </div>

        <!-- Pick Destination Mode Banner -->
        <div class="sandbox-pick-banner" id="sandboxPickBanner" style="display: none;">
          <span class="spb-icon">🎯</span>
          <span class="spb-text" id="sandboxPickBannerText">Click any hole on the board to set destination for Jump</span>
          <button type="button" class="btn-xs btn-ghost" data-action="cancel-pick-dest">✕ Cancel</button>
        </div>

        <!-- Interactive Board Viewport -->
        <div class="sandbox-board-viewport" id="sandboxBoardViewport">
          <div class="sandbox-board-svg-wrap" id="sandboxSvgWrap"></div>
        </div>

        <!-- Space Palette Tray -->
        <div class="sandbox-tray-wrap">
          <div class="sandbox-tray-title">Space Palette (Drag onto board or click to select & place):</div>
          <div class="sandbox-tray-items" id="sandboxTrayItems">
            ${ClashSandboxModal.renderTrayButtons()}
          </div>
        </div>

        <!-- Action Footer -->
        <div class="sandbox-footer">
          <div class="sandbox-footer-hint" id="sandboxFooterHint">
            💡 <em>Click any placed space to remove/reposition. Click ⚡ Jump or 🎯 to set destination.</em>
          </div>
          <div class="sandbox-footer-actions">
            <button type="button" class="btn-secondary" data-action="close">Cancel</button>
            <button type="button" class="btn-secondary sandbox-btn-quick-save" data-action="open-save" title="Save layout to library">
              💾 Save to Library
            </button>
            <button type="button" class="btn-primary btn-gold sandbox-btn-apply" data-action="apply-and-save">
              ✔ Apply Board Layout
            </button>
          </div>
        </div>
      </div>

      <!-- Custom Layouts Library Drawer Overlay -->
      <div class="sandbox-drawer-overlay" id="sandboxDrawerOverlay" style="display: none;">
        <div class="sandbox-drawer" id="sandboxDrawer">
          <div class="sandbox-drawer-header">
            <div class="sandbox-drawer-title-wrap">
              <span class="sandbox-drawer-icon">📁</span>
              <div>
                <h3 class="sandbox-drawer-title">${brand === 'pints' ? 'My Custom Drinking Boards' : (brand === 'adult' ? 'My Custom Adult Boards' : 'My Custom Track Layouts')}</h3>
                <div class="sandbox-drawer-sub">Saved on this device</div>
              </div>
            </div>
            <div class="sandbox-drawer-header-actions">
              <button type="button" class="btn-sm btn-gold" data-action="open-save" data-is-current="true">➕ Save Current</button>
              <button type="button" class="sandbox-drawer-close" data-action="close-drawer" title="Close Drawer">✕</button>
            </div>
          </div>
          <div class="sandbox-drawer-body" id="sandboxDrawerList">
            ${ClashSandboxModal.renderCustomLayoutCards()}
          </div>
        </div>
      </div>

      <!-- Save / Rename Dialog Overlay -->
      <div class="sandbox-dialog-overlay" id="sandboxSaveDialogOverlay" style="display: none;">
        <div class="sandbox-dialog-box" id="sandboxSaveDialogBox">
          <div class="sandbox-dialog-header">
            <span class="sandbox-dialog-icon">💾</span>
            <div>
              <h3 class="sandbox-dialog-title" id="sandboxSaveDialogTitle">Save Custom Track Layout</h3>
              <div class="sandbox-dialog-sub">Enter a unique name for this layout.</div>
            </div>
            <button type="button" class="sandbox-dialog-close" data-action="close-save-dialog">✕</button>
          </div>
          <div class="sandbox-dialog-body">
            <div class="sandbox-form-group">
              <label class="sandbox-form-label" for="txtCustomLayoutName">Layout Name</label>
              <input type="text" class="sandbox-input" id="txtCustomLayoutName" placeholder="e.g. PubCrawl120" maxlength="40" pattern="[a-zA-Z0-9]+">
              <div class="sandbox-form-error" id="saveLayoutError" style="display: none;"></div>
            </div>
          </div>
          <div class="sandbox-dialog-footer" id="sandboxSaveDialogFooter">
            <button type="button" class="btn-secondary" data-action="close-save-dialog">Cancel</button>
            <button type="button" class="btn-primary btn-gold" id="btnConfirmSaveLayout" data-action="submit-save-layout" data-as-new="false">
              💾 Save Layout
            </button>
          </div>
        </div>
      </div>

      <!-- Import JSON Dialog Overlay -->
      <div class="sandbox-dialog-overlay" id="sandboxImportDialogOverlay" style="display: none;">
        <div class="sandbox-dialog-box" id="sandboxImportDialogBox">
          <div class="sandbox-dialog-header">
            <span class="sandbox-dialog-icon">📥</span>
            <div>
              <h3 class="sandbox-dialog-title">Import Track Layout</h3>
              <div class="sandbox-dialog-sub">Paste track layout JSON shared by friends or exported previously.</div>
            </div>
            <button type="button" class="sandbox-dialog-close" data-action="close-import-dialog">✕</button>
          </div>
          <div class="sandbox-dialog-body">
            <div class="sandbox-form-group">
              <label class="sandbox-form-label" for="txtImportLayoutJson">Layout JSON</label>
              <textarea class="sandbox-textarea" id="txtImportLayoutJson" rows="6" placeholder="Paste { ... } or [ ... ] JSON here..."></textarea>
              <div class="sandbox-form-error" id="importLayoutError" style="display: none;"></div>
            </div>
          </div>
          <div class="sandbox-dialog-footer">
            <button type="button" class="btn-secondary" data-action="close-import-dialog">Cancel</button>
            <button type="button" class="btn-primary btn-gold" data-action="submit-import-layout">
              📥 Import Track
            </button>
          </div>
        </div>
      </div>

      <!-- Export / Share JSON Dialog Overlay -->
      <div class="sandbox-dialog-overlay" id="sandboxExportDialogOverlay" style="display: none;">
        <div class="sandbox-dialog-box" id="sandboxExportDialogBox">
          <div class="sandbox-dialog-header">
            <span class="sandbox-dialog-icon">📤</span>
            <div>
              <h3 class="sandbox-dialog-title" id="sandboxExportDialogTitle">Share Track Layout</h3>
              <div class="sandbox-dialog-sub" id="sandboxExportDialogSub">Copy track JSON or download as a file to share with friends.</div>
            </div>
            <button type="button" class="sandbox-dialog-close" data-action="close-export-dialog">✕</button>
          </div>
          <div class="sandbox-dialog-body">
            <div class="sandbox-form-group">
              <label class="sandbox-form-label" for="txtExportLayoutJson">Track Layout JSON</label>
              <textarea class="sandbox-textarea" id="txtExportLayoutJson" rows="7" readonly style="cursor: text; user-select: all;"></textarea>
              <div class="sandbox-form-help" id="sandboxExportHelp" style="margin-top: 6px; font-size: 0.8rem; color: #94a3b8;">
                Click inside to select all, or use the buttons below.
              </div>
            </div>
          </div>
          <div class="sandbox-dialog-footer" style="display: flex; gap: 8px; justify-content: space-between; flex-wrap: wrap;">
            <div style="display: flex; gap: 8px;">
              <button type="button" class="btn-secondary btn-sm" id="btnDownloadLayoutJson" data-action="download-export-layout" title="Download layout as a .json file">
                💾 Download .json
              </button>
              <button type="button" class="btn-secondary btn-sm" id="btnSystemShareLayout" style="display: none;" data-action="system-share-layout" title="Share via device share menu">
                🔗 Share...
              </button>
            </div>
            <div style="display: flex; gap: 8px;">
              <button type="button" class="btn-secondary btn-sm" data-action="close-export-dialog">Close</button>
              <button type="button" class="btn-primary btn-gold btn-sm" id="btnCopyExportJson" data-action="copy-export-json">
                📋 Copy Layout
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Jump Vector / Destination Dialog Overlay -->
      <div class="sandbox-dialog-overlay" id="sandboxJumpDialogOverlay" style="display: none;">
        <div class="sandbox-dialog-box" id="sandboxJumpDialogBox" style="max-width: 440px;">
          <div class="sandbox-dialog-header">
            <span class="sandbox-dialog-icon">⚡</span>
            <div>
              <h3 class="sandbox-dialog-title" id="sandboxJumpDialogTitle">Configure Jump Vector</h3>
              <div class="sandbox-dialog-sub">Set trigger and destination holes for this shortcut or trap.</div>
            </div>
            <button type="button" class="sandbox-dialog-close" data-action="close-jump-dialog">✕</button>
          </div>
          <div class="sandbox-dialog-body">
            <input type="hidden" id="txtJumpItemId" />
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
              <div class="sandbox-form-group">
                <label class="sandbox-form-label" for="numJumpStart">⚡ Trigger Hole</label>
                <input type="number" class="sandbox-input" id="numJumpStart" min="1" max="${targetVal - 1}" />
              </div>
              <div class="sandbox-form-group">
                <label class="sandbox-form-label" for="numJumpDest">🎯 Destination Hole</label>
                <input type="number" class="sandbox-input" id="numJumpDest" min="1" max="${targetVal - 1}" />
              </div>
            </div>

            <div style="margin-bottom: 12px;">
              <label class="sandbox-form-label" style="display: block; margin-bottom: 6px;">⚡ Quick Offsets</label>
              <div style="display: flex; gap: 6px; flex-wrap: wrap;">
                <button type="button" class="btn-xs btn-ghost jump-offset-btn" data-action="apply-jump-offset" data-offset="15">+15</button>
                <button type="button" class="btn-xs btn-ghost jump-offset-btn" data-action="apply-jump-offset" data-offset="30">+30</button>
                <button type="button" class="btn-xs btn-ghost jump-offset-btn" data-action="apply-jump-offset" data-offset="50">+50</button>
                <button type="button" class="btn-xs btn-ghost jump-offset-btn" data-action="apply-jump-offset" data-offset="-15">-15</button>
                <button type="button" class="btn-xs btn-ghost jump-offset-btn" data-action="apply-jump-offset" data-offset="-30">-30</button>
              </div>
            </div>

            <div id="jumpVectorSummary" style="padding: 10px 14px; border-radius: 8px; font-size: 0.88rem; margin-bottom: 14px; font-weight: 600; text-align: center; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.3);">
            </div>

            <div style="display: flex; gap: 8px;">
              <button type="button" class="btn-secondary btn-sm" style="flex: 1; padding: 8px;" data-action="start-pick-dest">
                🎯 Pick Destination on Board
              </button>
            </div>

            <div class="sandbox-form-error" id="jumpConfigError" style="display: none; margin-top: 8px;"></div>
          </div>
          <div class="sandbox-dialog-footer" style="display: flex; justify-content: space-between;">
            <button type="button" class="btn-danger btn-sm" data-action="delete-jump" title="Delete this jump from the board">
              🗑️ Remove Jump
            </button>
            <div style="display: flex; gap: 8px;">
              <button type="button" class="btn-secondary btn-sm" data-action="close-jump-dialog">Cancel</button>
              <button type="button" class="btn-primary btn-gold btn-sm" data-action="save-jump-dialog">
                ✓ Apply Destination
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    ClashSandboxModal.renderBoardSvg();
    ClashSandboxModal.bindTrayEvents();
    ClashSandboxModal.bindModalEvents(overlay);
  }

  /**
   * Centralized event delegation and listener binder for the Sandbox modal.
   * Eliminates inline onclick/oninput/onkeydown attributes.
   * @param {HTMLElement} overlay
   */
  static bindModalEvents(overlay) {
    if (!overlay) return;

    overlay.addEventListener('click', (e) => {
      // Backdrop clicks
      if (e.target.id === 'sandboxDrawerOverlay') {
        ClashSandboxModal.closeLayoutsDrawer();
        return;
      }
      if (e.target.id === 'sandboxSaveDialogOverlay') {
        ClashSandboxModal.closeSaveDialog();
        return;
      }
      if (e.target.id === 'sandboxImportDialogOverlay') {
        ClashSandboxModal.closeImportDialog();
        return;
      }
      if (e.target.id === 'sandboxExportDialogOverlay') {
        ClashSandboxModal.closeExportDialog();
        return;
      }
      if (e.target.id === 'sandboxJumpDialogOverlay') {
        ClashSandboxModal.closeJumpConfigDialog();
        return;
      }

      // Drawer list action delegation
      if (e.target.closest('#sandboxDrawerList')) {
        ClashSandboxModal.handleDrawerAction(e);
        return;
      }

      const actionBtn = e.target.closest('[data-action]');
      if (!actionBtn) return;

      const action = actionBtn.dataset.action;
      switch (action) {
        case 'apply-preset': {
          const key = actionBtn.dataset.preset;
          if (key) ClashSandboxModal.applyPresetKey(key);
          break;
        }
        case 'close':
          ClashSandboxModal.close();
          break;
        case 'randomize':
          ClashSandboxModal.randomizeLayout();
          break;
        case 'clear-all':
          ClashSandboxModal.clearAllHazards();
          break;
        case 'open-drawer':
          ClashSandboxModal.openLayoutsDrawer();
          break;
        case 'close-drawer':
          ClashSandboxModal.closeLayoutsDrawer();
          break;
        case 'open-save': {
          const isCurrent = actionBtn.dataset.isCurrent === 'true';
          ClashSandboxModal.openSaveDialog(isCurrent);
          break;
        }
        case 'close-save-dialog':
          ClashSandboxModal.closeSaveDialog();
          break;
        case 'submit-save-layout': {
          const asNew = actionBtn.dataset.asNew === 'true';
          ClashSandboxModal.submitSaveLayout(asNew);
          break;
        }
        case 'cancel-pick-dest':
          ClashSandboxModal.cancelPickDestinationMode();
          break;
        case 'apply-and-save':
          ClashSandboxModal.applyAndSave();
          break;
        case 'close-import-dialog':
          ClashSandboxModal.closeImportDialog();
          break;
        case 'submit-import-layout':
          ClashSandboxModal.submitImportLayout();
          break;
        case 'close-export-dialog':
          ClashSandboxModal.closeExportDialog();
          break;
        case 'download-export-layout':
          ClashSandboxModal.downloadExportedLayout();
          break;
        case 'system-share-layout':
          ClashSandboxModal.systemShareExportedLayout();
          break;
        case 'copy-export-json':
          ClashSandboxModal.copyExportedJsonToClipboard();
          break;
        case 'close-jump-dialog':
          ClashSandboxModal.closeJumpConfigDialog();
          break;
        case 'apply-jump-offset': {
          const offset = parseInt(actionBtn.dataset.offset, 10);
          if (!isNaN(offset)) ClashSandboxModal.applyJumpOffset(offset);
          break;
        }
        case 'start-pick-dest':
          ClashSandboxModal.startPickDestinationMode();
          break;
        case 'delete-jump':
          ClashSandboxModal.deleteCurrentJump();
          break;
        case 'save-jump-dialog':
          ClashSandboxModal.saveJumpConfigDialog();
          break;
      }
    });

    const txtName = overlay.querySelector('#txtCustomLayoutName');
    if (txtName) {
      txtName.addEventListener('input', () => {
        txtName.value = txtName.value.replace(/[^a-zA-Z0-9]/g, '');
      });
      txtName.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') ClashSandboxModal.submitSaveLayout();
      });
    }

    const txtExport = overlay.querySelector('#txtExportLayoutJson');
    if (txtExport) {
      txtExport.addEventListener('click', () => txtExport.select());
    }

    const numStart = overlay.querySelector('#numJumpStart');
    if (numStart) {
      numStart.addEventListener('input', () => ClashSandboxModal.onJumpInputChanged());
    }
    const numDest = overlay.querySelector('#numJumpDest');
    if (numDest) {
      numDest.addEventListener('input', () => ClashSandboxModal.onJumpInputChanged());
    }
  }

  /**
   * Renders the draggable/selectable buttons for each space type in the palette.
   * @returns {string}
   */
  static renderTrayButtons() {
    const brand = ClashSandboxModal.brand || 'clash';
    const palette = BRAND_ITEM_PALETTES[brand] || BRAND_ITEM_PALETTES.clash;
    const layout = ClashSandboxModal.activeLayout || [];

    const counts = {};
    layout.forEach(item => {
      counts[item.type] = (counts[item.type] || 0) + 1;
    });

    return palette.map(p => {
      const placed = counts[p.type] || 0;
      const limit = ClashSandboxModal.getItemLimit(p.type, brand);
      const isMaxed = placed >= limit;
      const isSelected = ClashSandboxModal.selectedTrayType === p.type && !isMaxed;

      const title = isMaxed
        ? `${p.name} — Maximum capacity reached (${placed}/${limit}) as defined in ClashSettings.`
        : `${p.name} (${p.desc}) — ${placed}/${limit} placed. Click to select, then click any hole; or drag onto hole.`;

      const badgeStyle = isMaxed
        ? 'background: rgba(239, 68, 68, 0.2); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.5); font-weight: 700;'
        : `background: ${p.color}33; color: ${p.color}; border: 1px solid ${p.color}66;`;

      return `
        <div class="sandbox-tray-btn ${isSelected ? 'is-selected' : ''} ${isMaxed ? 'disabled is-maxed' : ''}"
             data-hazard-type="${p.type}"
             data-placed="${placed}"
             data-limit="${limit}"
             draggable="${isMaxed ? 'false' : 'true'}"
             title="${escapeHtml(title)}">
          <span class="tray-icon">${p.icon}</span>
          <span class="tray-name">${escapeHtml(p.name)}</span>
          <span class="tray-count ${isMaxed ? 'is-maxed' : ''}" style="${badgeStyle}">
            ${placed} / ${limit}
          </span>
        </div>
      `;
    }).join('');
  }

  /**
   * Renders the SVG board with interactive holes and placed space tokens.
   */
  static renderBoardSvg() {
    if (typeof document === 'undefined') return;
    const container = document.getElementById('sandboxSvgWrap');
    if (!container) return;
    container.innerHTML = '';

    const targetVal = ClashSandboxModal.targetVal;
    const is121 = targetVal === 121;
    const clashCfg = is121 ? null : getClashBoardCfg(targetVal);
    const viewW = is121 ? 820 : clashCfg.viewW;
    const viewH = is121 ? 216 : clashCfg.viewH;
    const totalHoles = targetVal - 1;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${viewW} ${viewH}`);
    svg.setAttribute('class', 'sandbox-svg-board');

    const bgX = 15;
    const bgY = 12;
    const bgW = viewW - 30;
    const bgH = viewH - 24;
    const bgRx = 16;

    svg.innerHTML = `
      <defs>
        <linearGradient id="sbBoardWoodGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="#eed5ad" />
          <stop offset="45%" stop-color="#e2c191" />
          <stop offset="80%" stop-color="#d4af7a" />
          <stop offset="100%" stop-color="#c69e67" />
        </linearGradient>
        <marker id="sbArrowGreen" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#22c55e" />
        </marker>
        <marker id="sbArrowRed" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#ef4444" />
        </marker>
      </defs>
      <rect x="${bgX}" y="${bgY}" width="${bgW}" height="${bgH}" rx="${bgRx}"
            fill="url(#sbBoardWoodGrad)" stroke="#6b3a12" stroke-width="3" />
    `;

    const milestonesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    milestonesGroup.id = 'sbMilestones';
    svg.appendChild(milestonesGroup);

    const jumpsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    jumpsGroup.id = 'sbJumps';
    svg.appendChild(jumpsGroup);

    const holesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    holesGroup.id = 'sbHoles';
    svg.appendChild(holesGroup);

    const tokensGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    tokensGroup.id = 'sbHazardTokens';
    svg.appendChild(tokensGroup);

    container.appendChild(svg);

    // Milestones (every 5 holes)
    if (is121) {
      for (let h = 5; h <= 120; h += 5) {
        const pt = getTrackXY(h, 'p1', 121);
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', pt.x);
        line.setAttribute('y1', pt.y - 7);
        line.setAttribute('x2', pt.x);
        line.setAttribute('y2', pt.y + 7);
        line.setAttribute('stroke', '#6d421a');
        line.setAttribute('stroke-opacity', '0.55');
        line.setAttribute('stroke-width', '1.5');
        milestonesGroup.appendChild(line);
      }
    } else {
      for (let r = 0; r < clashCfg.numRows; r++) {
        const y = clashCfg.stY[r];
        const startH = r * 60 + 1;
        for (let g = 1; g <= 11; g++) {
          const h = startH + (g * 5) - 1;
          const pt = getTrackXY(h, 'p1', targetVal);
          const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          line.setAttribute('x1', pt.x);
          line.setAttribute('y1', y - 8);
          line.setAttribute('x2', pt.x);
          line.setAttribute('y2', y + 8);
          line.setAttribute('stroke', '#6d421a');
          line.setAttribute('stroke-opacity', '0.5');
          line.setAttribute('stroke-width', '1');
          milestonesGroup.appendChild(line);
        }
      }
    }

    // Snap indicator circle
    const snapIndicator = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    snapIndicator.id = 'sbSnapIndicator';
    snapIndicator.setAttribute('cx', '-100');
    snapIndicator.setAttribute('cy', '-100');
    snapIndicator.setAttribute('r', is121 ? '8.5' : '7.5');
    snapIndicator.setAttribute('fill', 'none');
    snapIndicator.setAttribute('stroke', '#38bdf8');
    snapIndicator.setAttribute('stroke-width', '2.5');
    snapIndicator.setAttribute('stroke-dasharray', '4,3');
    snapIndicator.setAttribute('opacity', '0');
    snapIndicator.style.pointerEvents = 'none';
    snapIndicator.style.transition = 'opacity 0.15s ease';
    svg.appendChild(snapIndicator);

    const coordsMap = ClashSandboxModal.getHoleCoordinatesMap(targetVal);

    // Render Holes
    for (let h = 1; h <= totalHoles; h++) {
      const pt = coordsMap[h];
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', pt.x);
      circle.setAttribute('cy', pt.y);
      circle.setAttribute('r', is121 ? '3.8' : '3.2');
      circle.setAttribute('class', 'sb-hole');
      circle.setAttribute('data-hole', h);

      // Hole click: place selected item, or set destination if in picking mode
      circle.addEventListener('click', (e) => {
        e.stopPropagation();
        if (ClashSandboxModal.pickingDestForJumpId) {
          ClashSandboxModal.setJumpDestination(ClashSandboxModal.pickingDestForJumpId, h);
          return;
        }
        if (ClashSandboxModal.selectedTrayType) {
          ClashSandboxModal.addHazardToHole(ClashSandboxModal.selectedTrayType, h);
        }
      });

      holesGroup.appendChild(circle);
    }

    // Delegated Board Drag & Drop: Smooth snapping to nearest hole, 0 freezing, 0 navigation bugs
    svg.addEventListener('dragover', (e) => {
      e.preventDefault();
      try {
        e.dataTransfer.dropEffect = 'copy';
      } catch (_) {}
      const svgPt = ClashSandboxModal.getSvgCoords(e, svg);
      const nearest = ClashSandboxModal.findClosestHole(svgPt.x, svgPt.y, targetVal);
      ClashSandboxModal.updateSnapHighlight(nearest, svg);
    });

    svg.addEventListener('dragleave', (e) => {
      if (!svg.contains(e.relatedTarget)) {
        ClashSandboxModal.clearSnapHighlight(svg);
      }
    });

    svg.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const svgPt = ClashSandboxModal.getSvgCoords(e, svg);
      const nearest = ClashSandboxModal.findClosestHole(svgPt.x, svgPt.y, targetVal);
      ClashSandboxModal.clearSnapHighlight(svg);

      let hazardType = null;
      try {
        hazardType = e.dataTransfer.getData('text/plain');
      } catch (_) {}
      if (!hazardType && ClashSandboxModal.draggedTrayType) {
        hazardType = ClashSandboxModal.draggedTrayType;
      }

      if (ClashSandboxModal.draggedDestOfJump) {
        ClashSandboxModal.draggedDestOfJump.dest = nearest;
        ClashSandboxModal.draggedDestOfJump = null;
        ClashSandboxModal.refreshView();
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast(`🎯 Jump destination set to Hole ${nearest}`);
        }
      } else if (ClashSandboxModal.draggedItem) {
        ClashSandboxModal.draggedItem.hole = nearest;
        ClashSandboxModal.draggedItem = null;
        ClashSandboxModal.refreshView();
      } else if (hazardType && hazardType !== 'jump_dest') {
        ClashSandboxModal.addHazardToHole(hazardType, nearest);
      }
    });

    const viewport = document.getElementById('sandboxBoardViewport');
    if (viewport && !viewport._hasDragGuard) {
      viewport._hasDragGuard = true;
      viewport.addEventListener('dragover', (e) => {
        e.preventDefault();
      });
      viewport.addEventListener('drop', (e) => {
        e.preventDefault();
        ClashSandboxModal.clearSnapHighlight(svg);
      });
    }

    // Render Jump Vector Arrows (for Clash & custom layouts)
    const jumps = (ClashSandboxModal.activeLayout || []).filter(i => i.type === 'jump' && i.dest);
    for (const j of jumps) {
      const p1 = getTrackXY(j.hole, 'p1', targetVal);
      const p2 = getTrackXY(j.dest, 'p1', targetVal);
      const isForward = j.dest > j.hole;
      const marker = isForward ? 'url(#sbArrowGreen)' : 'url(#sbArrowRed)';
      const strokeColor = isForward ? '#22c55e' : '#ef4444';

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const cx = (p1.x + p2.x) / 2 - dy * 0.15;
      const cy = (p1.y + p2.y) / 2 + dx * 0.15;
      path.setAttribute('d', `M ${p1.x} ${p1.y} Q ${cx} ${cy} ${p2.x} ${p2.y}`);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', strokeColor);
      path.setAttribute('stroke-width', '2.5');
      path.setAttribute('stroke-dasharray', '5,3');
      path.setAttribute('marker-end', marker);
      path.setAttribute('opacity', '0.85');
      path.setAttribute('class', 'sb-jump-path');
      path.setAttribute('data-jump-id', j.id);
      path.style.cursor = 'pointer';

      const pathTitle = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      pathTitle.textContent = `Jump: Hole ${j.hole} ➔ Hole ${j.dest} (${isForward ? `+${j.dest - j.hole} Forward Boost` : `${j.dest - j.hole} Backward Trap`}). Click to configure.`;
      path.appendChild(pathTitle);

      path.addEventListener('click', (e) => {
        e.stopPropagation();
        ClashSandboxModal.openJumpConfigDialog(j.id);
      });

      jumpsGroup.appendChild(path);
    }

    // Render Placed Tokens (Trigger spaces)
    const layout = ClashSandboxModal.activeLayout || [];
    for (const item of layout) {
      const pt = getTrackXY(item.hole, 'p1', targetVal);
      const token = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      token.setAttribute('class', `sb-token-group ${item.type === 'jump' ? 'is-jump' : ''}`);
      token.setAttribute('data-item-id', item.id);
      token.setAttribute('transform', `translate(${pt.x}, ${pt.y})`);

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('r', is121 ? '9.5' : '8.5');
      circle.setAttribute('fill', item.color || '#f0c64a');
      circle.setAttribute('stroke', '#111827');
      circle.setAttribute('stroke-width', '1.5');
      circle.setAttribute('class', 'sb-token-circle');

      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'central');
      text.setAttribute('font-size', is121 ? '10' : '9');
      text.textContent = item.icon || '★';

      const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      if (item.type === 'jump') {
        title.textContent = `⚡ Jump Trigger: Hole ${item.hole} ➔ Destination: Hole ${item.dest || '?'}. Click to configure destination or drag to move.`;
      } else {
        title.textContent = `${item.name} (Hole ${item.hole}). Click to remove or drag to move.`;
      }
      token.appendChild(title);

      token.appendChild(circle);
      token.appendChild(text);

      // Token click: jump opens config dialog; others remove (or Shift-click removes jump)
      token.addEventListener('click', (e) => {
        e.stopPropagation();
        if (e.shiftKey) {
          ClashSandboxModal.removeHazard(item.id);
          return;
        }
        if (item.type === 'jump') {
          ClashSandboxModal.openJumpConfigDialog(item.id);
        } else {
          ClashSandboxModal.removeHazard(item.id);
        }
      });

      // Token drag: reposition start hole (native DnD)
      token.setAttribute('draggable', 'true');
      token.addEventListener('dragstart', (e) => {
        ClashSandboxModal.draggedItem = item;
        ClashSandboxModal.draggedDestOfJump = null;
        try {
          e.dataTransfer.setData('application/json', JSON.stringify(item));
          e.dataTransfer.setData('text/plain', item.type);
          e.dataTransfer.effectAllowed = 'move';
        } catch (_) {}
      });
      token.addEventListener('dragend', () => {
        ClashSandboxModal.draggedItem = null;
        ClashSandboxModal.clearSnapHighlight();
      });

      // Universal Pointer Drag (smooth touch & mouse drag without browser freezes)
      ClashSandboxModal.bindTokenPointerDrag(token, item);

      tokensGroup.appendChild(token);
    }

    // Render Jump Destination Tokens (Target pins)
    for (const j of jumps) {
      const pt = getTrackXY(j.dest, 'p1', targetVal);
      const destToken = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      destToken.setAttribute('class', 'sb-token-group sb-jump-dest-token');
      destToken.setAttribute('data-jump-id', j.id);
      destToken.setAttribute('transform', `translate(${pt.x}, ${pt.y})`);

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('r', is121 ? '8.5' : '7.5');
      circle.setAttribute('fill', '#0284c7');
      circle.setAttribute('stroke', '#ffffff');
      circle.setAttribute('stroke-width', '1.5');
      circle.setAttribute('class', 'sb-token-circle sb-dest-circle');

      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'central');
      text.setAttribute('font-size', is121 ? '8.5' : '7.5');
      text.textContent = '🎯';

      const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      title.textContent = `🎯 Jump Destination: Hole ${j.dest} (from Hole ${j.hole}). Click to configure, or drag to another hole.`;
      destToken.appendChild(title);

      destToken.appendChild(circle);
      destToken.appendChild(text);

      // Click destination pin: open config dialog
      destToken.addEventListener('click', (e) => {
        e.stopPropagation();
        if (e.shiftKey) {
          ClashSandboxModal.removeHazard(j.id);
          return;
        }
        ClashSandboxModal.openJumpConfigDialog(j.id);
      });

      // Drag destination pin: reposition jump destination (native DnD)
      destToken.setAttribute('draggable', 'true');
      destToken.addEventListener('dragstart', (e) => {
        ClashSandboxModal.draggedDestOfJump = j;
        ClashSandboxModal.draggedItem = null;
        try {
          e.dataTransfer.setData('application/json', JSON.stringify({ isDest: true, jumpId: j.id }));
          e.dataTransfer.setData('text/plain', 'jump_dest');
          e.dataTransfer.effectAllowed = 'move';
        } catch (_) {}
      });
      destToken.addEventListener('dragend', () => {
        ClashSandboxModal.draggedDestOfJump = null;
        ClashSandboxModal.clearSnapHighlight();
      });

      // Universal Pointer Drag for Jump Destination Pin
      ClashSandboxModal.bindDestPinPointerDrag(destToken, j);

      tokensGroup.appendChild(destToken);
    }
  }

  /**
   * Binds pointer-based dragging (mouse/touch/pen) for placed board hazard tokens.
   */
  static bindTokenPointerDrag(tokenEl, item) {
    let startPos = null;
    let isDragging = false;
    let ghostEl = null;

    const onMove = (e) => {
      if (!startPos) return;
      const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY = e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
      const dx = clientX - startPos.x;
      const dy = clientY - startPos.y;

      if (!isDragging && (dx * dx + dy * dy > 36)) {
        isDragging = true;
        ghostEl = document.createElement('div');
        ghostEl.className = 'sb-floating-drag-ghost';
        ghostEl.innerHTML = `<span style="font-size: 1.1rem;">${item.icon || '★'}</span><span>${escapeHtml(item.name || item.type)}</span>`;
        ghostEl.style.left = `${clientX}px`;
        ghostEl.style.top = `${clientY}px`;
        ghostEl.style.borderColor = item.color || '#f0c64a';
        document.body.appendChild(ghostEl);
      }

      if (isDragging && ghostEl) {
        ghostEl.style.left = `${clientX}px`;
        ghostEl.style.top = `${clientY}px`;
        const svg = document.querySelector('.sandbox-svg-board');
        if (svg) {
          const rect = svg.getBoundingClientRect();
          if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
            const svgPt = ClashSandboxModal.getSvgCoords({ clientX, clientY }, svg);
            const nearest = ClashSandboxModal.findClosestHole(svgPt.x, svgPt.y, ClashSandboxModal.targetVal);
            ClashSandboxModal.updateSnapHighlight(nearest, svg);
          } else {
            ClashSandboxModal.clearSnapHighlight(svg);
          }
        }
      }
    };

    const onUp = (e) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);

      if (isDragging) {
        const clientX = e.clientX !== undefined ? e.clientX : (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : (startPos ? startPos.x : 0));
        const clientY = e.clientY !== undefined ? e.clientY : (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : (startPos ? startPos.y : 0));
        const svg = document.querySelector('.sandbox-svg-board');
        let droppedHole = null;
        if (svg) {
          const rect = svg.getBoundingClientRect();
          if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
            const svgPt = ClashSandboxModal.getSvgCoords({ clientX, clientY }, svg);
            droppedHole = ClashSandboxModal.findClosestHole(svgPt.x, svgPt.y, ClashSandboxModal.targetVal);
          }
        }
        if (ghostEl && ghostEl.parentNode) ghostEl.parentNode.removeChild(ghostEl);
        ghostEl = null;
        isDragging = false;
        startPos = null;
        ClashSandboxModal.clearSnapHighlight();

        if (droppedHole && droppedHole !== item.hole) {
          item.hole = droppedHole;
          ClashSandboxModal.refreshView();
          if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
            window.showToast(`Moved ${item.name} to Hole ${droppedHole}`);
          }
        }
      } else {
        startPos = null;
      }
    };

    tokenEl.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      startPos = { x: e.clientX, y: e.clientY };
      isDragging = false;
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    });
  }

  /**
   * Binds pointer-based dragging (mouse/touch/pen) for jump destination pins.
   */
  static bindDestPinPointerDrag(destTokenEl, jump) {
    let startPos = null;
    let isDragging = false;
    let ghostEl = null;

    const onMove = (e) => {
      if (!startPos) return;
      const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY = e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
      const dx = clientX - startPos.x;
      const dy = clientY - startPos.y;

      if (!isDragging && (dx * dx + dy * dy > 36)) {
        isDragging = true;
        ghostEl = document.createElement('div');
        ghostEl.className = 'sb-floating-drag-ghost';
        ghostEl.innerHTML = `<span style="font-size: 1.1rem;">🎯</span><span>Jump Destination</span>`;
        ghostEl.style.left = `${clientX}px`;
        ghostEl.style.top = `${clientY}px`;
        ghostEl.style.borderColor = '#0284c7';
        document.body.appendChild(ghostEl);
      }

      if (isDragging && ghostEl) {
        ghostEl.style.left = `${clientX}px`;
        ghostEl.style.top = `${clientY}px`;
        const svg = document.querySelector('.sandbox-svg-board');
        if (svg) {
          const rect = svg.getBoundingClientRect();
          if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
            const svgPt = ClashSandboxModal.getSvgCoords({ clientX, clientY }, svg);
            const nearest = ClashSandboxModal.findClosestHole(svgPt.x, svgPt.y, ClashSandboxModal.targetVal);
            ClashSandboxModal.updateSnapHighlight(nearest, svg);
          } else {
            ClashSandboxModal.clearSnapHighlight(svg);
          }
        }
      }
    };

    const onUp = (e) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);

      if (isDragging) {
        const clientX = e.clientX !== undefined ? e.clientX : (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : (startPos ? startPos.x : 0));
        const clientY = e.clientY !== undefined ? e.clientY : (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : (startPos ? startPos.y : 0));
        const svg = document.querySelector('.sandbox-svg-board');
        let droppedHole = null;
        if (svg) {
          const rect = svg.getBoundingClientRect();
          if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
            const svgPt = ClashSandboxModal.getSvgCoords({ clientX, clientY }, svg);
            droppedHole = ClashSandboxModal.findClosestHole(svgPt.x, svgPt.y, ClashSandboxModal.targetVal);
          }
        }
        if (ghostEl && ghostEl.parentNode) ghostEl.parentNode.removeChild(ghostEl);
        ghostEl = null;
        isDragging = false;
        startPos = null;
        ClashSandboxModal.clearSnapHighlight();

        if (droppedHole && droppedHole !== jump.dest) {
          jump.dest = droppedHole;
          ClashSandboxModal.refreshView();
          if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
            window.showToast(`🎯 Jump destination set to Hole ${droppedHole}`);
          }
        }
      } else {
        startPos = null;
      }
    };

    destTokenEl.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      startPos = { x: e.clientX, y: e.clientY };
      isDragging = false;
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    });
  }

  /**
   * Binds click & drag events to tray buttons.
   * Supports both HTML5 drag and smooth Pointer Events dragging for desktop and mobile.
   */
  static bindTrayEvents() {
    const btns = document.querySelectorAll('.sandbox-tray-btn');
    const brand = ClashSandboxModal.brand || 'clash';
    const palette = BRAND_ITEM_PALETTES[brand] || BRAND_ITEM_PALETTES.clash;

    btns.forEach(btn => {
      const type = btn.getAttribute('data-hazard-type');
      const def = palette.find(p => p.type === type) || { name: type, icon: '★', color: '#f0c64a' };

      // HTML5 Drag support
      btn.addEventListener('dragstart', (e) => {
        const limit = ClashSandboxModal.getItemLimit(type, brand);
        const placed = (ClashSandboxModal.activeLayout || []).filter(i => i.type === type).length;
        if (placed >= limit) {
          e.preventDefault();
          if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
            window.showToast(`⚠️ Maximum ${limit} ${def.name} allowed (${limit} max defined in ClashSettings).`);
          }
          return;
        }
        try {
          e.dataTransfer.setData('text/plain', type);
          e.dataTransfer.effectAllowed = 'copy';
        } catch (_) {}
        ClashSandboxModal.selectedTrayType = type;
        ClashSandboxModal.draggedTrayType = type;
      });

      btn.addEventListener('dragend', () => {
        ClashSandboxModal.draggedTrayType = null;
        ClashSandboxModal.clearSnapHighlight();
      });

      // Pointer drag support (works seamlessly on desktop mouse, trackpad, and touch devices)
      let pointerDownPos = null;
      let isPointerDragging = false;
      let ghostEl = null;

      const onPointerMove = (e) => {
        if (!pointerDownPos) return;
        const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        const clientY = e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
        const dx = clientX - pointerDownPos.x;
        const dy = clientY - pointerDownPos.y;

        if (!isPointerDragging && (dx * dx + dy * dy > 36)) {
          isPointerDragging = true;
          ClashSandboxModal.selectedTrayType = type;
          ClashSandboxModal.draggedTrayType = type;

          ghostEl = document.createElement('div');
          ghostEl.className = 'sb-floating-drag-ghost';
          ghostEl.innerHTML = `<span style="font-size: 1.1rem;">${def.icon}</span><span>${escapeHtml(def.name)}</span>`;
          ghostEl.style.left = `${clientX}px`;
          ghostEl.style.top = `${clientY}px`;
          ghostEl.style.borderColor = def.color || '#f0c64a';
          document.body.appendChild(ghostEl);
        }

        if (isPointerDragging && ghostEl) {
          ghostEl.style.left = `${clientX}px`;
          ghostEl.style.top = `${clientY}px`;

          const svg = document.querySelector('.sandbox-svg-board');
          if (svg) {
            const rect = svg.getBoundingClientRect();
            if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
              const svgPt = ClashSandboxModal.getSvgCoords({ clientX, clientY }, svg);
              const nearest = ClashSandboxModal.findClosestHole(svgPt.x, svgPt.y, ClashSandboxModal.targetVal);
              ClashSandboxModal.updateSnapHighlight(nearest, svg);
            } else {
              ClashSandboxModal.clearSnapHighlight(svg);
            }
          }
        }
      };

      const onPointerUp = (e) => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);

        if (isPointerDragging) {
          const clientX = e.clientX !== undefined ? e.clientX : (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : (pointerDownPos ? pointerDownPos.x : 0));
          const clientY = e.clientY !== undefined ? e.clientY : (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : (pointerDownPos ? pointerDownPos.y : 0));
          const svg = document.querySelector('.sandbox-svg-board');
          let droppedHole = null;
          if (svg) {
            const rect = svg.getBoundingClientRect();
            if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
              const svgPt = ClashSandboxModal.getSvgCoords({ clientX, clientY }, svg);
              droppedHole = ClashSandboxModal.findClosestHole(svgPt.x, svgPt.y, ClashSandboxModal.targetVal);
            }
          }
          if (ghostEl && ghostEl.parentNode) ghostEl.parentNode.removeChild(ghostEl);
          ghostEl = null;
          isPointerDragging = false;
          pointerDownPos = null;
          ClashSandboxModal.draggedTrayType = null;
          ClashSandboxModal.clearSnapHighlight();

          if (droppedHole) {
            ClashSandboxModal.addHazardToHole(type, droppedHole);
          }
        } else {
          // Regular click / tap
          pointerDownPos = null;
          const limit = ClashSandboxModal.getItemLimit(type, brand);
          const placed = (ClashSandboxModal.activeLayout || []).filter(i => i.type === type).length;
          if (placed >= limit) {
            if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
              window.showToast(`⚠️ Maximum ${limit} ${def.name} reached (${placed}/${limit}) as defined in ClashSettings.`);
            }
            return;
          }
          if (ClashSandboxModal.selectedTrayType === type) {
            ClashSandboxModal.selectedTrayType = null;
            btn.classList.remove('is-selected');
          } else {
            btns.forEach(b => b.classList.remove('is-selected'));
            ClashSandboxModal.selectedTrayType = type;
            btn.classList.add('is-selected');
          }
        }
      };

      btn.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        const limit = ClashSandboxModal.getItemLimit(type, brand);
        const placed = (ClashSandboxModal.activeLayout || []).filter(i => i.type === type).length;
        if (placed >= limit) {
          if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
            window.showToast(`⚠️ Maximum ${limit} ${def.name} reached (${placed}/${limit}) as defined in ClashSettings.`);
          }
          return;
        }
        pointerDownPos = { x: e.clientX, y: e.clientY };
        isPointerDragging = false;
        window.addEventListener('pointermove', onPointerMove, { passive: true });
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
      });
    });
  }

  /**
   * Places a space/hazard onto a specific hole.
   * @param {string} type
   * @param {number} hole
   */
  static addHazardToHole(type, hole) {
    if (!ClashSandboxModal.activeLayout) ClashSandboxModal.activeLayout = [];
    const brand = ClashSandboxModal.brand || 'clash';
    const limit = ClashSandboxModal.getItemLimit(type, brand);
    const currentPlaced = ClashSandboxModal.activeLayout.filter(i => i.type === type).length;

    const palette = BRAND_ITEM_PALETTES[brand] || BRAND_ITEM_PALETTES.clash;
    const def = palette.find(p => p.type === type) || { name: type.toUpperCase(), icon: '★', color: '#f0c64a', length: 1 };

    if (currentPlaced >= limit) {
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(`⚠️ Maximum ${limit} ${def.name} allowed (${limit} max defined in ClashSettings).`);
      }
      return;
    }

    const newItem = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      type,
      name: `${def.name}`,
      icon: def.icon,
      color: def.color,
      hole,
      length: def.length || 1
    };

    if (type === 'jump') {
      const defaultDelta = ClashSandboxModal.targetVal === 121 ? 15 : 30;
      newItem.dest = Math.min(ClashSandboxModal.targetVal - 5, hole + defaultDelta);
    }

    ClashSandboxModal.activeLayout.push(newItem);

    // Auto-deselect if limit reached
    if (currentPlaced + 1 >= limit && ClashSandboxModal.selectedTrayType === type) {
      ClashSandboxModal.selectedTrayType = null;
    }

    ClashSandboxModal.refreshView();

    if (type === 'jump') {
      ClashSandboxModal.openJumpConfigDialog(newItem.id);
    }
  }

  /**
   * Removes a placed space by ID.
   * @param {string} itemId
   */
  static removeHazard(itemId) {
    ClashSandboxModal.activeLayout = ClashSandboxModal.activeLayout.filter(i => i.id !== itemId);
    ClashSandboxModal.refreshView();
  }

  /**
   * Clears all placed spaces from the sandbox board.
   */
  static clearAllHazards() {
    ClashSandboxModal.activeLayout = [];
    ClashSandboxModal.cancelPickDestinationMode();
    ClashSandboxModal.refreshView();
  }

  /**
   * Opens the Jump Vector Configuration Dialog.
   * @param {string} jumpId
   */
  static openJumpConfigDialog(jumpId) {
    if (typeof document === 'undefined') return;
    const layout = ClashSandboxModal.activeLayout || [];
    const jump = layout.find(i => i.id === jumpId && i.type === 'jump');
    if (!jump) return;

    const overlay = document.getElementById('sandboxJumpDialogOverlay');
    const txtId = document.getElementById('txtJumpItemId');
    const numStart = document.getElementById('numJumpStart');
    const numDest = document.getElementById('numJumpDest');
    const title = document.getElementById('sandboxJumpDialogTitle');
    const err = document.getElementById('jumpConfigError');

    if (!overlay || !txtId || !numStart || !numDest) return;

    txtId.value = jump.id;
    numStart.value = jump.hole;
    numDest.value = jump.dest || (jump.hole + (ClashSandboxModal.targetVal === 121 ? 15 : 30));
    const maxHole = ClashSandboxModal.targetVal - 1;
    numStart.max = maxHole;
    numDest.max = maxHole;

    if (title) title.textContent = `Configure Jump (${jump.name || 'Jump'})`;
    if (err) err.style.display = 'none';

    ClashSandboxModal.onJumpInputChanged();

    overlay.style.display = 'flex';
    setTimeout(() => {
      numDest.focus();
      numDest.select();
    }, 50);
  }

  /**
   * Closes the Jump Vector Configuration Dialog.
   */
  static closeJumpConfigDialog() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('sandboxJumpDialogOverlay');
    if (overlay) overlay.style.display = 'none';
  }

  /**
   * Real-time update for Jump summary box when start or destination hole changes.
   */
  static onJumpInputChanged() {
    if (typeof document === 'undefined') return;
    const numStart = document.getElementById('numJumpStart');
    const numDest = document.getElementById('numJumpDest');
    const summary = document.getElementById('jumpVectorSummary');
    if (!numStart || !numDest || !summary) return;

    const s = parseInt(numStart.value, 10);
    const d = parseInt(numDest.value, 10);
    if (isNaN(s) || isNaN(d)) {
      summary.innerHTML = '<span style="color: #94a3b8;">Enter valid hole numbers.</span>';
      summary.style.background = 'rgba(0,0,0,0.3)';
      summary.style.borderColor = 'rgba(255,255,255,0.1)';
      return;
    }

    const delta = d - s;
    if (delta > 0) {
      summary.style.background = 'rgba(34, 197, 94, 0.15)';
      summary.style.borderColor = '#22c55e';
      summary.style.color = '#4ade80';
      summary.innerHTML = `🟢 <strong>Forward Shortcut: +${delta} Holes</strong><br><span style="font-size: 0.8rem; font-weight: normal; color: #bbf7d0;">Peg landing on Hole ${s} leaps forward to Hole ${d}.</span>`;
    } else if (delta < 0) {
      summary.style.background = 'rgba(239, 68, 68, 0.15)';
      summary.style.borderColor = '#ef4444';
      summary.style.color = '#f87171';
      summary.innerHTML = `🔴 <strong>Backward Trap: ${delta} Holes</strong><br><span style="font-size: 0.8rem; font-weight: normal; color: #fecaca;">Peg landing on Hole ${s} is thrown backward to Hole ${d}.</span>`;
    } else {
      summary.style.background = 'rgba(245, 158, 11, 0.15)';
      summary.style.borderColor = '#f59e0b';
      summary.style.color = '#fbbf24';
      summary.innerHTML = `⚠️ <strong>Zero Jump</strong><br><span style="font-size: 0.8rem; font-weight: normal; color: #fef08a;">Trigger and destination cannot be the same hole.</span>`;
    }
  }

  /**
   * Applies a relative offset to destination hole.
   * @param {number} offset
   */
  static applyJumpOffset(offset) {
    if (typeof document === 'undefined') return;
    const numStart = document.getElementById('numJumpStart');
    const numDest = document.getElementById('numJumpDest');
    if (!numStart || !numDest) return;
    const s = parseInt(numStart.value, 10) || 1;
    const maxHole = ClashSandboxModal.targetVal - 1;
    const newDest = Math.max(1, Math.min(maxHole, s + offset));
    numDest.value = newDest;
    ClashSandboxModal.onJumpInputChanged();
  }

  /**
   * Saves updated jump trigger and destination from the dialog.
   */
  static saveJumpConfigDialog() {
    if (typeof document === 'undefined') return;
    const txtId = document.getElementById('txtJumpItemId');
    const numStart = document.getElementById('numJumpStart');
    const numDest = document.getElementById('numJumpDest');
    const err = document.getElementById('jumpConfigError');
    if (!txtId || !numStart || !numDest) return;

    const jumpId = txtId.value;
    const layout = ClashSandboxModal.activeLayout || [];
    const jump = layout.find(i => i.id === jumpId && i.type === 'jump');
    if (!jump) return;

    const s = parseInt(numStart.value, 10);
    const d = parseInt(numDest.value, 10);
    const maxHole = ClashSandboxModal.targetVal - 1;

    if (isNaN(s) || s < 1 || s > maxHole) {
      if (err) { err.textContent = `Trigger hole must be between 1 and ${maxHole}.`; err.style.display = 'block'; }
      return;
    }
    if (isNaN(d) || d < 1 || d > maxHole) {
      if (err) { err.textContent = `Destination hole must be between 1 and ${maxHole}.`; err.style.display = 'block'; }
      return;
    }
    if (s === d) {
      if (err) { err.textContent = 'Destination hole must be different from trigger hole.'; err.style.display = 'block'; }
      return;
    }

    jump.hole = s;
    jump.dest = d;
    ClashSandboxModal.closeJumpConfigDialog();
    ClashSandboxModal.refreshView();

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      const delta = d - s;
      const typeStr = delta > 0 ? `+${delta} Forward Shortcut` : `${delta} Backward Trap`;
      window.showToast(`⚡ Jump set: Hole ${s} ➔ Hole ${d} (${typeStr})`);
    }
  }

  /**
   * Deletes the currently inspected jump from the board.
   */
  static deleteCurrentJump() {
    if (typeof document === 'undefined') return;
    const txtId = document.getElementById('txtJumpItemId');
    const jumpId = txtId ? txtId.value : null;
    if (jumpId) {
      ClashSandboxModal.removeHazard(jumpId);
    }
    ClashSandboxModal.closeJumpConfigDialog();
  }

  /**
   * Enters "Pick Destination on Board" interactive mode.
   */
  static startPickDestinationMode() {
    if (typeof document === 'undefined') return;
    const txtId = document.getElementById('txtJumpItemId');
    const jumpId = txtId ? txtId.value : null;
    if (!jumpId) return;

    const layout = ClashSandboxModal.activeLayout || [];
    const jump = layout.find(i => i.id === jumpId && i.type === 'jump');
    if (!jump) return;

    ClashSandboxModal.pickingDestForJumpId = jumpId;
    ClashSandboxModal.closeJumpConfigDialog();

    const banner = document.getElementById('sandboxPickBanner');
    const bannerText = document.getElementById('sandboxPickBannerText');
    const svgWrap = document.getElementById('sandboxSvgWrap');

    if (bannerText) {
      bannerText.innerHTML = `🎯 Click any hole on the board to set destination for Jump (Trigger: <strong>Hole ${jump.hole}</strong>)`;
    }
    if (banner) banner.style.display = 'flex';
    if (svgWrap) svgWrap.classList.add('sandbox-picking-active');

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`🎯 Click any hole on the track to set Jump destination`);
    }
  }

  /**
   * Cancels "Pick Destination on Board" mode.
   */
  static cancelPickDestinationMode() {
    ClashSandboxModal.pickingDestForJumpId = null;
    if (typeof document === 'undefined') return;
    const banner = document.getElementById('sandboxPickBanner');
    const svgWrap = document.getElementById('sandboxSvgWrap');
    if (banner) banner.style.display = 'none';
    if (svgWrap) svgWrap.classList.remove('sandbox-picking-active');
  }

  /**
   * Sets jump destination from a board click.
   * @param {string} jumpId
   * @param {number} destHole
   */
  static setJumpDestination(jumpId, destHole) {
    const layout = ClashSandboxModal.activeLayout || [];
    const jump = layout.find(i => i.id === jumpId && i.type === 'jump');
    if (!jump) {
      ClashSandboxModal.cancelPickDestinationMode();
      return;
    }

    if (destHole === jump.hole) {
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast('⚠️ Destination hole must be different from trigger hole.');
      }
      return;
    }

    jump.dest = destHole;
    ClashSandboxModal.cancelPickDestinationMode();
    ClashSandboxModal.refreshView();

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      const delta = destHole - jump.hole;
      const typeStr = delta > 0 ? `+${delta} Forward Shortcut` : `${delta} Backward Trap`;
      window.showToast(`🎯 Jump destination set to Hole ${destHole} (${typeStr})`);
    }
  }


  /**
   * Applies one of the curated presets to the sandbox layout.
   * @param {string} presetKey
   */
  static applyPresetKey(presetKey) {
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const presets = engine.getPresets(ClashSandboxModal.targetVal);
    const preset = presets[presetKey] || presets.standard;
    if (preset) {
      ClashSandboxModal.activeLayout = ClashSandboxModal.convertPresetToItems(preset.config, brand);
      ClashSandboxModal.refreshView();
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(`Applied preset: ${preset.name} (${preset.badge})`);
      }
    }
  }

  /**
   * Generates a full random non-overlapping distribution.
   */
  static randomizeLayout() {
    const brand = ClashSandboxModal.brand || 'clash';
    const target = ClashSandboxModal.targetVal;
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const presets = engine.getPresets(target);
    const standard = presets.standard.config;
    const items = ClashSandboxModal.convertPresetToItems(standard, brand);

    // Filter items to respect dropdown count limits from board layout options
    const counts = {};
    const filteredItems = [];
    for (const item of items) {
      const limit = ClashSandboxModal.getItemLimit(item.type, brand);
      const curr = counts[item.type] || 0;
      if (curr < limit) {
        counts[item.type] = curr + 1;
        filteredItems.push(item);
      }
    }

    const usedHoles = new Set();
    const randomized = [];
    const minHole = target === 121 ? 5 : 10;
    const maxHole = target === 121 ? 118 : target - 30;

    for (const item of filteredItems) {
      let tries = 0;
      let placedHole = null;
      while (tries < 100) {
        tries++;
        const candidate = Math.floor(Math.random() * (maxHole - minHole)) + minHole;
        let conflict = false;
        const radius = target === 121 ? 2 : 4;
        for (let h = candidate - radius; h <= candidate + radius; h++) {
          if (usedHoles.has(h)) { conflict = true; break; }
        }
        if (!conflict) {
          placedHole = candidate;
          for (let h = candidate - radius; h <= candidate + radius; h++) usedHoles.add(h);
          break;
        }
      }
      if (placedHole) {
        item.hole = placedHole;
        if (item.dest != null) {
          item.dest = Math.min(target - 5, placedHole + (Math.random() > 0.25 ? 30 : -15));
        }
        randomized.push(item);
      }
    }

    ClashSandboxModal.activeLayout = randomized;
    ClashSandboxModal.refreshView();
  }

  /**
   * Generates HTML for the live metrics bar.
   * @returns {string}
   */
  static renderMetricsBarHtml() {
    const brand = ClashSandboxModal.brand || 'clash';
    const targetVal = ClashSandboxModal.targetVal;
    const items = ClashSandboxModal.activeLayout || [];

    if (brand === 'pints') {
      const m = PintsPresets.computeTrackMetrics(items, targetVal);
      return `
        <div class="sandbox-metric-chip">
          <span class="sm-val">${m.totalDrinkSpaces}</span>
          <span class="sm-label">Total Drink Spaces</span>
        </div>
        <div class="sandbox-metric-chip">
          <span class="sm-val" style="color: #10b981;">${m.socialDrinks}</span>
          <span class="sm-label">Social / Group Drinks</span>
        </div>
        <div class="sandbox-metric-chip">
          <span class="sm-val" style="color: #3b82f6;">${m.targetedDrinks}</span>
          <span class="sm-label">Targeted Shots</span>
        </div>
        <div class="sandbox-metric-chip">
          <span class="sm-val" style="color: #ec4899;">${m.genderDrinks}</span>
          <span class="sm-label">Guys vs Gals</span>
        </div>
        <div class="sandbox-metric-chip">
          <span class="sm-val" style="color: #f59e0b;">~${m.avgSpacing}h</span>
          <span class="sm-label">Avg Spacing</span>
        </div>
      `;
    }

    if (brand === 'adult') {
      const m = AdultPresets.computeTrackMetrics(items, targetVal);
      return `
        <div class="sandbox-metric-chip">
          <span class="sm-val">${m.totalDareSpaces}</span>
          <span class="sm-label">Total Dare Spaces</span>
        </div>
        <div class="sandbox-metric-chip">
          <span class="sm-val" style="color: #f43f5e;">${m.stripSpaces}</span>
          <span class="sm-label">Strip Spaces</span>
        </div>
        <div class="sandbox-metric-chip">
          <span class="sm-val" style="color: #ec4899;">${m.timedDares}</span>
          <span class="sm-label">Timed Challenges</span>
        </div>
        <div class="sandbox-metric-chip">
          <span class="sm-val" style="color: #10b981;">~${m.avgSpacing}h</span>
          <span class="sm-label">Avg Spacing</span>
        </div>
      `;
    }

    // Clash metrics
    const m = ClashPresets.computeTrackMetrics(items, targetVal);
    return `
      <div class="sandbox-metric-chip">
        <span class="sm-val">${m.totalHazards}</span>
        <span class="sm-label">Total Hazards</span>
      </div>
      <div class="sandbox-metric-chip">
        <span class="sm-val" style="color: #ef4444;">${m.penaltyTraps}</span>
        <span class="sm-label">Traps (Jail/Bar/Time)</span>
      </div>
      <div class="sandbox-metric-chip">
        <span class="sm-val" style="color: #3b82f6;">${m.forwardBoosts}</span>
        <span class="sm-label">Forward Boosts</span>
      </div>
      <div class="sandbox-metric-chip">
        <span class="sm-val" style="color: #f59e0b;">${m.chokePoints + m.stopSigns}</span>
        <span class="sm-label">Choke Points & Stops</span>
      </div>
      <div class="sandbox-metric-chip">
        <span class="sm-val" style="color: #10b981;">~${m.avgSpacing}h</span>
        <span class="sm-label">Avg Spacing</span>
      </div>
    `;
  }

  /**
   * Re-renders the SVG board, updates the palette counts, and refreshes the live analysis chips.
   */
  static refreshView() {
    if (typeof document === 'undefined') return;
    ClashSandboxModal.renderBoardSvg();
    const tray = document.getElementById('sandboxTrayItems');
    if (tray) {
      tray.innerHTML = ClashSandboxModal.renderTrayButtons();
      ClashSandboxModal.bindTrayEvents();
    }
    const metricsBar = document.getElementById('sandboxMetricsBar');
    if (metricsBar) {
      metricsBar.innerHTML = ClashSandboxModal.renderMetricsBarHtml();
    }
  }

  /**
   * Backward-compatibility alias for updating analysis and view.
   */
  static updateTrackAnalysis() {
    ClashSandboxModal.refreshView();
  }

  /**
   * Builds form configuration object from obstacle items list.
   * @param {Array<Object>} [layout]
   * @param {number} [targetVal]
   * @param {string} [brand=ClashSandboxModal.brand]
   * @returns {Object}
   */
  static buildFormConfig(layout = ClashSandboxModal.activeLayout, targetVal = ClashSandboxModal.targetVal, brand = ClashSandboxModal.brand) {
    const items = layout || [];
    const cfg = {};
    const grouped = {};
    for (const item of items) {
      if (!grouped[item.type]) grouped[item.type] = [];
      grouped[item.type].push(item);
    }

    if (brand === 'pints') {
      const palette = BRAND_ITEM_PALETTES.pints;
      palette.forEach(opt => {
        const typeList = grouped[opt.type] || [];
        const chkKey = `opt${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}`;
        cfg[chkKey] = Boolean(typeList.length);
        cfg[`${chkKey}Count`] = typeList.length;
        cfg[`${opt.type}Count`] = typeList.length;
        cfg[`${opt.type}Holes`] = typeList.map(it => it.hole);
        cfg[`${opt.type}Starts`] = cfg[`${opt.type}Holes`];
        for (let idx = 0; idx < 4; idx++) {
          cfg[`${chkKey}${idx + 1}Hole`] = typeList[idx] ? typeList[idx].hole : 0;
        }
      });
      return cfg;
    }

    if (brand === 'adult') {
      const palette = BRAND_ITEM_PALETTES.adult;
      palette.forEach(opt => {
        const typeList = grouped[opt.type] || [];
        const chkKey = `opt${opt.type.charAt(0).toUpperCase() + opt.type.slice(1)}`;
        cfg[chkKey] = Boolean(typeList.length);
        cfg[`${chkKey}Count`] = typeList.length;
        cfg[`${opt.type}Count`] = typeList.length;
        cfg[`${opt.type}Holes`] = typeList.map(it => it.hole);
        cfg[`${opt.type}Starts`] = cfg[`${opt.type}Holes`];
        for (let idx = 0; idx < 4; idx++) {
          cfg[`${chkKey}${idx + 1}Hole`] = typeList[idx] ? typeList[idx].hole : 0;
        }
      });
      return cfg;
    }

    // Default: Clash - dynamically use maxCount from ClashSettings.getBlockDefs()
    const clashDefs = ClashSandboxModal.getClashBlockDefs();
    const processedTypes = new Set();

    for (const def of clashDefs) {
      const type = def.type;
      processedTypes.add(type);
      const typeItems = grouped[type] || [];
      const maxCount = typeof def.maxCount === 'number' ? def.maxCount : (def.type === 'jump' || def.type === 'stopSign' || def.type === 'signs' ? 8 : 4);
      const count = Math.min(maxCount, typeItems.length);
      const isEnabled = count > 0;

      // 1. Toggles
      cfg[def.chkId] = isEnabled;
      if (type === 'jump') cfg.optJumps = isEnabled;
      if (type === 'noPassingLanes') cfg.optNoPassing = isEnabled;
      if (type === 'jail') cfg.optJails = isEnabled;
      if (type === 'bar') cfg.optBars = isEnabled;
      if (type === 'timeout') cfg.optTimeouts = isEnabled;
      if (type === 'detour') cfg.optDetours = isEnabled;
      if (type === 'roundAbout') cfg.optRoundAbouts = isEnabled;
      if (type === 'signs') cfg.optSign = isEnabled;
      if (type === 'stopSign') cfg.optStopSigns = isEnabled;
      if (type === 'wheel') cfg.optSpinningWheel = isEnabled;

      // 2. Count fields
      cfg[def.countId] = count;
      cfg[`${type}Count`] = count;
      if (type === 'noPassingLanes') {
        cfg.noPassingLaneCount = count;
        cfg.noPassingLanesCount = count;
      }
      if (type === 'wheel') {
        cfg.optSpinningWheelCount = count;
        cfg.spinningWheelCount = count;
      }

      // 3. Array fields (starts / holes)
      const validItems = typeItems.slice(0, count);
      const holes = validItems.map(it => it.hole);
      cfg[`${type}Holes`] = holes;
      cfg[`${type}Starts`] = holes;
      if (type === 'noPassingLanes') {
        cfg.noPassingLaneStarts = holes;
        cfg.noPassingLaneHoles = holes;
      }
      if (type === 'wheel') {
        cfg.spinningWheelHoles = holes;
        cfg.spinningWheelStarts = holes;
      }

      // 4. Indexed hole/start fields up to def.maxCount
      const suffix = def.selectIdSuffix || 'Hole';
      const prefix = def.selectIdPrefix || `opt${type.charAt(0).toUpperCase() + type.slice(1)}`;
      for (let i = 1; i <= maxCount; i++) {
        const item = validItems[i - 1];
        const h = (item && i <= count) ? item.hole : 0;
        cfg[`${prefix}${i}${suffix}`] = h;
        cfg[`${type}${i}${suffix}`] = h;
      }

      // 5. Special Jump Destinations
      if (type === 'jump') {
        cfg.jumpDests = validItems.map(it => it.dest || Math.min(targetVal - 5, it.hole + 30));
        for (let i = 1; i <= maxCount; i++) {
          const item = validItems[i - 1];
          const d = (item && i <= count) ? (item.dest || Math.min(targetVal - 5, item.hole + 30)) : 0;
          cfg[`optJump${i}Dest`] = d;
          cfg[`jump${i}Dest`] = d;
        }
      }

      // 6. Special Signs Blocks
      if (type === 'signs') {
        cfg.signsBlocks = validItems.map((item, i) => ({
          id: i + 1,
          start: item.hole,
          size: (i + 1) % 2 === 0 ? 4 : 2,
          signs: (i + 1) % 2 === 0 ? ['Spin The Wheel', 'Plus 10', 'Minus 5', 'Score Plus x2'] : ['Plus 5', 'Roll The Dice']
        }));
      }
    }

    return cfg;
  }

  /**
   * Opens the Custom Layouts slide-over drawer.
   */
  static openLayoutsDrawer() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('sandboxDrawerOverlay');
    if (!overlay) return;
    ClashSandboxModal.refreshDrawerList();
    overlay.style.display = 'flex';
  }

  /**
   * Closes the Custom Layouts slide-over drawer.
   */
  static closeLayoutsDrawer() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('sandboxDrawerOverlay');
    if (overlay) overlay.style.display = 'none';
  }

  /**
   * Refreshes the custom layouts list inside the drawer.
   */
  static refreshDrawerList() {
    if (typeof document === 'undefined') return;
    const list = document.getElementById('sandboxDrawerList');
    if (list) {
      list.innerHTML = ClashSandboxModal.renderCustomLayoutCards();
    }
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const count = (engine.getCustomLayouts ? engine.getCustomLayouts() : []).length;
    const badge = document.getElementById('sandboxCustomLayoutCount');
    if (badge) badge.textContent = String(count);
  }

  /**
   * Renders the HTML cards for all saved custom layouts.
   * @returns {string}
   */
  static renderCustomLayoutCards() {
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const layouts = engine.getCustomLayouts ? engine.getCustomLayouts() : [];

    if (!layouts.length) {
      return `
        <div class="sandbox-drawer-empty">
          <span class="empty-icon">📂</span>
          <div class="empty-title">No Custom Layouts Yet</div>
          <div class="empty-desc">Create your favorite configuration and click <strong>"Save Layout"</strong> to save it here!</div>
        </div>
      `;
    }

    return layouts.map(l => {
      const isCurrent = ClashSandboxModal.activeCustomLayoutId === l.id;
      const dateStr = l.updatedAt ? new Date(l.updatedAt).toLocaleDateString() : '';

      let metricsChipsHtml = '';
      if (brand === 'pints') {
        const m = l.metrics || PintsPresets.computeTrackMetrics(l.items || [], l.target || 121);
        metricsChipsHtml = `
          <span class="cl-metric-chip" title="Total Drink Spaces">📍 ${m.totalDrinkSpaces || 0}</span>
          <span class="cl-metric-chip" title="Social Drinks" style="color: #10b981;">🥂 ${m.socialDrinks || 0}</span>
          <span class="cl-metric-chip" title="Targeted Shots" style="color: #3b82f6;">🎯 ${m.targetedDrinks || 0}</span>
          <span class="cl-metric-chip" title="Guys vs Gals" style="color: #ec4899;">🥊 ${m.genderDrinks || 0}</span>
          <span class="cl-metric-chip" title="Average Spacing" style="color: #f59e0b;">~${m.avgSpacing || 0}h</span>
        `;
      } else if (brand === 'adult') {
        const m = l.metrics || AdultPresets.computeTrackMetrics(l.items || [], l.target || 121);
        metricsChipsHtml = `
          <span class="cl-metric-chip" title="Total Dare Spaces">📍 ${m.totalDareSpaces || 0}</span>
          <span class="cl-metric-chip" title="Strip Spaces" style="color: #f43f5e;">👙 ${m.stripSpaces || 0}</span>
          <span class="cl-metric-chip" title="Timed Challenges" style="color: #ec4899;">⏳ ${m.timedDares || 0}</span>
          <span class="cl-metric-chip" title="Average Spacing" style="color: #10b981;">~${m.avgSpacing || 0}h</span>
        `;
      } else {
        const m = l.metrics || ClashPresets.computeTrackMetrics(l.items || [], l.target || 481);
        metricsChipsHtml = `
          <span class="cl-metric-chip" title="Total Hazards">📍 ${m.totalHazards || 0}</span>
          <span class="cl-metric-chip" title="Penalty Traps" style="color: #ef4444;">🔒 ${m.penaltyTraps || 0}</span>
          <span class="cl-metric-chip" title="Forward Boosts" style="color: #3b82f6;">⚡ ${m.forwardBoosts || 0}</span>
          <span class="cl-metric-chip" title="Choke Points & Stops" style="color: #f59e0b;">🚫 ${(m.chokePoints || 0) + (m.stopSigns || 0)}</span>
          <span class="cl-metric-chip" title="Average Spacing" style="color: #10b981;">~${m.avgSpacing || 0}h</span>
        `;
      }

      const safeId = escapeHtml(l.id);
      return `
        <div class="custom-layout-card ${isCurrent ? 'is-active-card' : ''}" data-layout-id="${safeId}">
          <div class="cl-card-header">
            <div class="cl-card-info">
              <span class="cl-card-name" id="clName_${safeId}">${escapeHtml(l.name)}</span>
              ${isCurrent ? '<span class="cl-active-tag">Active</span>' : ''}
              <span class="cl-card-date">${escapeHtml(dateStr)}</span>
            </div>
            <span class="cl-target-tag">${Number(l.target) || (brand === 'clash' ? 481 : 121)} Holes</span>
          </div>
          <div class="cl-card-metrics">
            ${metricsChipsHtml}
          </div>
          <div class="cl-card-actions">
            <button type="button" class="btn-xs btn-gold cl-btn-load" data-layout-action="load" data-layout-id="${safeId}">
              📥 Load
            </button>
            <button type="button" class="btn-xs btn-ghost cl-btn-rename" data-layout-action="rename" data-layout-id="${safeId}" title="Rename layout">
              ✏️ Rename
            </button>
            <button type="button" class="btn-xs btn-ghost cl-btn-duplicate" data-layout-action="duplicate" data-layout-id="${safeId}" title="Duplicate layout">
              📋 Copy
            </button>
            <button type="button" class="btn-xs btn-danger cl-btn-delete" data-layout-action="delete" data-layout-id="${safeId}" title="Delete layout">
              🗑️
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  /**
   * Safe event delegation handler for custom layout card actions.
   * Prevents injection of executable code into inline HTML handlers.
   * @param {Event} event
   */
  static handleDrawerAction(event) {
    if (!event || !event.target) return;
    const btn = event.target.closest('[data-layout-action]');
    if (!btn) return;
    const action = btn.dataset.layoutAction;
    const layoutId = btn.dataset.layoutId;
    if (!layoutId) return;

    if (action === 'load') ClashSandboxModal.loadCustomLayout(layoutId);
    else if (action === 'rename') ClashSandboxModal.promptRenameLayout(layoutId);
    else if (action === 'duplicate') ClashSandboxModal.duplicateLayout(layoutId);
    else if (action === 'export') ClashSandboxModal.exportSingleLayout(layoutId);
    else if (action === 'delete') ClashSandboxModal.confirmDeleteLayout(layoutId);
  }

  /**
   * Loads a custom layout by ID into the active sandbox session.
   * @param {string} layoutId
   */
  static loadCustomLayout(layoutId) {
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const layouts = engine.getCustomLayouts ? engine.getCustomLayouts() : [];
    const layout = layouts.find(l => l.id === layoutId);
    const counts = {};
    const clampedItems = [];
    for (const item of (layout.items || [])) {
      const limit = ClashSandboxModal.getItemLimit(item.type, brand);
      const curr = counts[item.type] || 0;
      if (curr < limit) {
        counts[item.type] = curr + 1;
        clampedItems.push(item);
      }
    }
    ClashSandboxModal.activeLayout = JSON.parse(JSON.stringify(clampedItems));
    ClashSandboxModal.activeCustomLayoutId = layout.id;
    ClashSandboxModal.activeCustomLayoutName = layout.name;
    if (layout.target && [121, 241, 361, 481].includes(Number(layout.target))) {
      ClashSandboxModal.targetVal = Number(layout.target);
    }

    ClashSandboxModal.closeLayoutsDrawer();
    ClashSandboxModal.refreshView();
    ClashSandboxModal.updateHeaderActiveBadge();

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`📥 Loaded custom layout: "${layout.name}"`);
    }
  }

  /**
   * Opens the Save / Update Layout dialog.
   * @param {boolean} [isSaveAs=false]
   */
  static openSaveDialog(isSaveAs = false) {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('sandboxSaveDialogOverlay');
    const input = document.getElementById('txtCustomLayoutName');
    const title = document.getElementById('sandboxSaveDialogTitle');
    const footer = document.getElementById('sandboxSaveDialogFooter');
    const errorEl = document.getElementById('saveLayoutError');

    if (!overlay || !input) return;

    const isExisting = Boolean(ClashSandboxModal.activeCustomLayoutId && !isSaveAs);
    if (title) {
      title.textContent = isExisting
        ? `Update "${ClashSandboxModal.activeCustomLayoutName}"`
        : 'Save Custom Track Layout';
    }
    input.value = isExisting ? (ClashSandboxModal.activeCustomLayoutName || '').replace(/[^a-zA-Z0-9]/g, '') : '';
    input.addEventListener('input', () => {
      input.value = input.value.replace(/[^a-zA-Z0-9]/g, '');
      if (errorEl) errorEl.style.display = 'none';
    });
    if (errorEl) errorEl.style.display = 'none';

    if (footer) {
      if (isExisting) {
        footer.innerHTML = `
          <button type="button" class="btn-secondary" data-action="close-save-dialog">Cancel</button>
          <button type="button" class="btn-secondary" data-action="submit-save-layout" data-as-new="true">Save as New</button>
          <button type="button" class="btn-primary btn-gold" data-action="submit-save-layout" data-as-new="false">💾 Update Layout</button>
        `;
      } else {
        footer.innerHTML = `
          <button type="button" class="btn-secondary" data-action="close-save-dialog">Cancel</button>
          <button type="button" class="btn-primary btn-gold" data-action="submit-save-layout" data-as-new="false">💾 Save Layout</button>
        `;
      }
    }

    overlay.style.display = 'flex';
    setTimeout(() => input.focus(), 50);
  }

  /**
   * Closes the Save / Update Layout dialog.
   */
  static closeSaveDialog() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('sandboxSaveDialogOverlay');
    if (overlay) overlay.style.display = 'none';
  }

  /**
   * Submits custom layout saving.
   * @param {boolean} [saveAsNew=false]
   */
  static submitSaveLayout(saveAsNew = false) {
    if (typeof document === 'undefined') return;
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const input = document.getElementById('txtCustomLayoutName');
    const errorEl = document.getElementById('saveLayoutError');
    const name = input ? sanitizeUserInput(input.value).replace(/[^a-zA-Z0-9]/g, '').trim() : '';

    if (!name) {
      if (errorEl) {
        errorEl.textContent = 'Please enter a name for your track layout (letters and numbers only).';
        errorEl.style.display = 'block';
      }
      return;
    }

    const items = ClashSandboxModal.activeLayout || [];
    const metrics = engine.computeTrackMetrics(items, ClashSandboxModal.targetVal);
    const config = ClashSandboxModal.buildFormConfig(items, ClashSandboxModal.targetVal, brand);

    const saved = engine.saveCustomLayout({
      id: (!saveAsNew && ClashSandboxModal.activeCustomLayoutId) ? ClashSandboxModal.activeCustomLayoutId : undefined,
      name,
      target: ClashSandboxModal.targetVal,
      items,
      metrics,
      config
    });

    ClashSandboxModal.activeCustomLayoutId = saved.id;
    ClashSandboxModal.activeCustomLayoutName = saved.name;

    ClashSandboxModal.closeSaveDialog();
    ClashSandboxModal.refreshDrawerList();
    ClashSandboxModal.updateHeaderActiveBadge();

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`💾 Saved custom layout: "${saved.name}"`);
    }
  }

  /**
   * Prompts user to rename a custom layout.
   * @param {string} layoutId
   */
  static promptRenameLayout(layoutId) {
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const layouts = engine.getCustomLayouts ? engine.getCustomLayouts() : [];
    const layout = layouts.find(l => l.id === layoutId);
    if (!layout) return;

    const rawName = window.prompt(`Rename layout "${layout.name}":`, layout.name);
    if (!rawName) return;
    const newName = sanitizeUserInput(rawName).replace(/[^a-zA-Z0-9]/g, '').trim();
    if (!newName || newName === layout.name) return;

    engine.renameCustomLayout(layoutId, newName);
    if (ClashSandboxModal.activeCustomLayoutId === layoutId) {
      ClashSandboxModal.activeCustomLayoutName = newName;
      ClashSandboxModal.updateHeaderActiveBadge();
    }
    ClashSandboxModal.refreshDrawerList();

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`✏️ Renamed layout to "${newName}"`);
    }
  }

  /**
   * Prompts user to confirm deletion of a custom layout.
   * @param {string} layoutId
   */
  static confirmDeleteLayout(layoutId) {
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const layouts = engine.getCustomLayouts ? engine.getCustomLayouts() : [];
    const layout = layouts.find(l => l.id === layoutId);
    if (!layout) return;

    const confirmed = window.confirm(`Are you sure you want to delete custom layout "${layout.name}"?`);
    if (!confirmed) return;

    engine.deleteCustomLayout(layoutId);
    if (ClashSandboxModal.activeCustomLayoutId === layoutId) {
      ClashSandboxModal.activeCustomLayoutId = null;
      ClashSandboxModal.activeCustomLayoutName = null;
      ClashSandboxModal.updateHeaderActiveBadge();
    }
    ClashSandboxModal.refreshDrawerList();

    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`🗑️ Deleted layout "${layout.name}"`);
    }
  }

  /**
   * Duplicates an existing custom layout.
   * @param {string} layoutId
   */
  static duplicateLayout(layoutId) {
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const cloned = engine.duplicateCustomLayout ? engine.duplicateCustomLayout(layoutId) : null;
    if (cloned) {
      ClashSandboxModal.refreshDrawerList();
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(`📋 Duplicated layout as "${cloned.name}"`);
      }
    }
  }

  /**
   * Opens the Share / Export JSON Dialog for a specific layout.
   * @param {string} layoutId
   */
  static exportSingleLayout(layoutId) {
    ClashSandboxModal.openExportDialog(layoutId);
  }

  /**
   * Opens the Share / Export Dialog and auto-selects the track JSON.
   * @param {string} [layoutId=null]
   */
  static openExportDialog(layoutId = null) {
    if (typeof document === 'undefined') return;
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const layouts = engine.getCustomLayouts ? engine.getCustomLayouts() : [];
    let layout = null;
    let titleHtml = 'Share: <strong>All Custom Tracks</strong>';

    if (layoutId) {
      layout = layouts.find(l => l.id === layoutId);
      if (!layout) return;
      titleHtml = `Share Track: <strong>${escapeHtml(layout.name)}</strong>`;
    }

    const json = engine.exportLayout ? engine.exportLayout(layoutId) : JSON.stringify(LayoutSanitizer.prepareForExport(layout || layouts), null, 2);
    ClashSandboxModal.currentExportedLayout = { layout: layout || { name: `${brand}-tracks` }, json };

    const overlay = document.getElementById('sandboxExportDialogOverlay');
    const txt = document.getElementById('txtExportLayoutJson');
    const title = document.getElementById('sandboxExportDialogTitle');
    const btnShare = document.getElementById('btnSystemShareLayout');
    const btnCopy = document.getElementById('btnCopyExportJson');

    if (!overlay || !txt) return;

    if (title) {
      title.innerHTML = titleHtml;
    }
    txt.value = json;

    if (btnCopy) {
      btnCopy.textContent = '📋 Copy Layout';
      btnCopy.classList.remove('btn-success');
      btnCopy.classList.add('btn-gold');
    }

    if (btnShare) {
      btnShare.style.display = (typeof navigator !== 'undefined' && typeof navigator.share === 'function') ? 'inline-flex' : 'none';
    }

    overlay.style.display = 'flex';
    setTimeout(() => {
      txt.focus();
      txt.select();
    }, 50);

    ClashSandboxModal.copyExportedJsonToClipboard(false);
  }

  /**
   * Closes the Share / Export Dialog.
   */
  static closeExportDialog() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('sandboxExportDialogOverlay');
    if (overlay) overlay.style.display = 'none';
  }

  /**
   * Copies exported JSON to clipboard with multi-tier fallback.
   * @param {boolean} [showToast=true]
   */
  static copyExportedJsonToClipboard(showToast = true) {
    if (typeof document === 'undefined') return;
    const txt = document.getElementById('txtExportLayoutJson');
    const val = txt ? txt.value : (ClashSandboxModal.currentExportedLayout?.json || '');
    if (!val) return;

    const markSuccess = () => {
      const btnCopy = document.getElementById('btnCopyExportJson');
      if (btnCopy) {
        btnCopy.textContent = '✅ Copied!';
        btnCopy.classList.remove('btn-gold');
        btnCopy.classList.add('btn-success');
        setTimeout(() => {
          if (btnCopy) {
            btnCopy.textContent = '📋 Copy Layout';
            btnCopy.classList.remove('btn-success');
            btnCopy.classList.add('btn-gold');
          }
        }, 2500);
      }
      if (showToast && typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast('📋 Track layout JSON copied to clipboard!');
      }
    };

    if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(val).then(() => {
        markSuccess();
      }).catch(() => {
        try {
          if (txt) {
            txt.focus();
            txt.select();
            if (document.execCommand('copy')) markSuccess();
          }
        } catch (_) {}
      });
    } else {
      try {
        if (txt) {
          txt.focus();
          txt.select();
          if (document.execCommand('copy')) markSuccess();
        }
      } catch (_) {}
    }
  }

  /**
   * Downloads the exported layout as a .json file.
   */
  static downloadExportedLayout() {
    const exported = ClashSandboxModal.currentExportedLayout;
    if (!exported || !exported.json) return;

    try {
      const safeName = (exported.layout?.name || 'custom-track')
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'track-layout';
      const fileName = `${safeName}.json`;

      const blob = new Blob([exported.json], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(`💾 Downloaded "${fileName}"`);
      }
    } catch (err) {
      console.warn('[ClashSandboxModal] Download failed:', err);
    }
  }

  /**
   * Invokes native Web Share API if available.
   */
  static systemShareExportedLayout() {
    const exported = ClashSandboxModal.currentExportedLayout;
    if (!exported || !exported.json) return;

    if (navigator && typeof navigator.share === 'function') {
      navigator.share({
        title: `Track Layout: ${exported.layout?.name || 'Custom Track'}`,
        text: exported.json
      }).catch(err => {
        if (err.name !== 'AbortError') {
          console.warn('[ClashSandboxModal] System share failed:', err);
        }
      });
    }
  }

  /**
   * Opens the Import JSON Dialog.
   */
  static openImportDialog() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('sandboxImportDialogOverlay');
    const txt = document.getElementById('txtImportLayoutJson');
    const err = document.getElementById('importLayoutError');
    if (!overlay || !txt) return;
    txt.value = '';
    if (err) err.style.display = 'none';
    overlay.style.display = 'flex';
    setTimeout(() => txt.focus(), 50);
  }

  /**
   * Closes the Import JSON Dialog.
   */
  static closeImportDialog() {
    if (typeof document === 'undefined') return;
    const overlay = document.getElementById('sandboxImportDialogOverlay');
    if (overlay) overlay.style.display = 'none';
  }

  /**
   * Submits JSON import.
   */
  static submitImportLayout() {
    if (typeof document === 'undefined') return;
    const brand = ClashSandboxModal.brand || 'clash';
    const engine = ClashSandboxModal.getPresetEngine(brand);
    const txt = document.getElementById('txtImportLayoutJson');
    const err = document.getElementById('importLayoutError');
    const raw = txt ? txt.value.trim() : '';

    if (!raw) {
      if (err) {
        err.textContent = 'Please paste a valid JSON track layout.';
        err.style.display = 'block';
      }
      return;
    }

    if (raw.length > MAX_LAYOUT_JSON_LENGTH) {
      if (err) {
        err.textContent = `Layout JSON exceeds maximum allowed size (${MAX_LAYOUT_JSON_LENGTH / 1024}KB).`;
        err.style.display = 'block';
      }
      return;
    }

    try {
      const imported = engine.importLayout(raw);
      ClashSandboxModal.closeImportDialog();
      ClashSandboxModal.refreshDrawerList();
      if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
        window.showToast(`📥 Imported layout "${imported.name}" successfully!`);
      }
    } catch (e) {
      if (err) {
        err.textContent = `Invalid layout JSON: ${e.message}`;
        err.style.display = 'block';
      }
    }
  }

  /**
   * Updates the header badge showing the active custom layout name.
   */
  static updateHeaderActiveBadge() {
    if (typeof document === 'undefined') return;
    const badge = document.getElementById('sandboxActiveBadge');
    if (badge) {
      if (ClashSandboxModal.activeCustomLayoutName) {
        badge.innerHTML = `📁 Custom: <strong>${escapeHtml(ClashSandboxModal.activeCustomLayoutName)}</strong>`;
        badge.classList.add('is-visible');
      } else {
        badge.innerHTML = '';
        badge.classList.remove('is-visible');
      }
    }
  }

  /**
   * Applies the current sandbox layout to the settings store, form fields, and broadcasts across network.
   */
  static applyAndSave() {
    const brand = ClashSandboxModal.brand || 'clash';
    const layout = ClashSandboxModal.activeLayout || [];
    const cfg = ClashSandboxModal.buildFormConfig(layout, ClashSandboxModal.targetVal, brand);

    // Apply to DOM fields
    if (typeof document !== 'undefined') {
      for (const [prop, val] of Object.entries(cfg)) {
        if (typeof val === 'object' && val !== null) continue; // skip arrays/objects for scalar DOM elements
        const el = document.getElementById(prop);
        if (el) {
          if (el.type === 'checkbox') {
            el.checked = Boolean(val);
            const toggleFn = window[`toggle${prop.replace('opt', '')}Config`];
            if (typeof toggleFn === 'function') toggleFn(el.checked);
          } else {
            if (el.tagName === 'SELECT' && val !== undefined && val !== null) {
              const strVal = String(val);
              let opt = el.querySelector(`option[value="${strVal}"]`);
              if (!opt) {
                opt = document.createElement('option');
                opt.value = strVal;
                opt.textContent = el.id.endsWith('Count') ? `${strVal} Spaces` : `Hole ${strVal}`;
                el.appendChild(opt);
              }
            }
            el.value = String(val);
            el.dataset.customized = 'true';
            try {
              el.dispatchEvent(new Event('change', { bubbles: true }));
            } catch (_) {}
          }
        }
      }
      if (typeof window !== 'undefined' && typeof window.updateTrackItemRows === 'function') {
        if (brand === 'pints') {
          ['youDrink', 'guysDrink', 'galsDrink', 'leftDrink', 'rightDrink', 'everyoneDrink', 'everyoneButYouDrink', 'pickWhoDrinks'].forEach(k => window.updateTrackItemRows(k));
        } else if (brand === 'adult') {
          ['loseClothing', 'gainClothing', 'pickWhoLosesClothing', 'rightLosesClothing', 'leftLosesClothing', 'removeClothingChoice', 'touchBreast', 'lickBreast', 'suckBreast', 'touchGenitals', 'lickGenitals', 'suckGenitals', 'fingerGenitals'].forEach(k => window.updateTrackItemRows(k));
        } else if (brand === 'clash') {
          ['noPassingLanes', 'jump', 'jail', 'bar', 'timeout', 'detour', 'roundAbout', 'signs', 'stopSign', 'dice', 'wheel'].forEach(k => window.updateTrackItemRows(k));
        }
      }
    }

    if (typeof window !== 'undefined') {
      const customPresetProp = brand === 'pints' ? 'pintsPreset' : (brand === 'adult' ? 'adultPreset' : 'clashPreset');
      const customLayoutNameProp = brand === 'pints' ? 'pintsCustomLayoutName' : (brand === 'adult' ? 'adultCustomLayoutName' : 'clashCustomLayoutName');
      const customLayoutIdProp = brand === 'pints' ? 'pintsCustomLayoutId' : (brand === 'adult' ? 'adultCustomLayoutId' : 'clashCustomLayoutId');

      if (window.settings) {
        Object.assign(window.settings, cfg);
        window.settings[customPresetProp] = 'custom';
        if (ClashSandboxModal.activeCustomLayoutName) {
          window.settings[customLayoutNameProp] = ClashSandboxModal.activeCustomLayoutName;
          window.settings[customLayoutIdProp] = ClashSandboxModal.activeCustomLayoutId;
        }
      }

      if (window.crazyCribbageApp && window.crazyCribbageApp.settings) {
        Object.assign(window.crazyCribbageApp.settings, cfg);
        window.crazyCribbageApp.settings[customPresetProp] = 'custom';
        if (ClashSandboxModal.activeCustomLayoutName) {
          window.crazyCribbageApp.settings[customLayoutNameProp] = ClashSandboxModal.activeCustomLayoutName;
          window.crazyCribbageApp.settings[customLayoutIdProp] = ClashSandboxModal.activeCustomLayoutId;
        }
      }

      if (typeof window.saveBrandSettings === 'function') {
        const curBrand = (typeof window.readBrandSettings === 'function') ? window.readBrandSettings(brand) : {};
        const mergedBrand = Object.assign({}, curBrand, cfg);
        mergedBrand[customPresetProp] = 'custom';
        if (ClashSandboxModal.activeCustomLayoutName) {
          mergedBrand[customLayoutNameProp] = ClashSandboxModal.activeCustomLayoutName;
          mergedBrand[customLayoutIdProp] = ClashSandboxModal.activeCustomLayoutId;
        }
        window.saveBrandSettings(brand, mergedBrand);
      }

      if (typeof window.saveSettingsStore === 'function') {
        const curStore = (typeof window.readFormSettings === 'function') ? window.readFormSettings() : {};
        const mergedStore = Object.assign({}, curStore, window.settings, cfg);
        mergedStore[customPresetProp] = 'custom';
        if (ClashSandboxModal.activeCustomLayoutName) {
          mergedStore[customLayoutNameProp] = ClashSandboxModal.activeCustomLayoutName;
          mergedStore[customLayoutIdProp] = ClashSandboxModal.activeCustomLayoutId;
        }
        window.saveSettingsStore(mergedStore);
      }

      // If hosting an online room, sync to all clients
      if (typeof window.netPush === 'function') {
        window.netPush({
          type: 'trackLayout',
          brand,
          settings: window.settings,
          customLayoutName: ClashSandboxModal.activeCustomLayoutName || null
        });
      }

      // Live update 3D board obstacles and pints objects if active
      if (window._currentRenderer3D) {
        if (window._currentRenderer3D.options) {
          window._currentRenderer3D.options.obstacleConfigs = null;
          window._currentRenderer3D.options.pintsConfigs = null;
          window._currentRenderer3D.options.adultConfigs = null;
        }
        if (typeof window._currentRenderer3D.updateClashObstacles === 'function') {
          window._currentRenderer3D.updateClashObstacles(window.settings);
        }
        if (typeof window._currentRenderer3D.updatePintsObjects === 'function') {
          window._currentRenderer3D.updatePintsObjects(window.settings);
        }
        if (typeof window._currentRenderer3D.updateAdultObjects === 'function') {
          window._currentRenderer3D.updateAdultObjects(window.settings);
        }
      }

      // Rebuild board layout on active view
      if (typeof window.buildBoard === 'function') {
        window.buildBoard();
      } else if (window.crazyCribbageApp && typeof window.crazyCribbageApp.buildBoard === 'function') {
        window.crazyCribbageApp.buildBoard();
      }

      if (typeof window.renderScores === 'function') {
        window.renderScores(false);
      } else if (window.crazyCribbageApp && typeof window.crazyCribbageApp.renderScores === 'function') {
        window.crazyCribbageApp.renderScores(false);
      }

      if (typeof window.showToast === 'function') {
        const nameMsg = ClashSandboxModal.activeCustomLayoutName
          ? `🏁 Applied "${ClashSandboxModal.activeCustomLayoutName}" to match!`
          : '🏁 Custom Board Layout applied to match!';
        window.showToast(nameMsg);
      }
    }

    ClashSandboxModal.close();
  }
}

if (typeof window !== 'undefined') {
  window.ClashSandboxModal = ClashSandboxModal;
  window.openClashSandbox = (opts) => ClashSandboxModal.open({ brand: 'clash', ...opts });
  window.openPintsSandbox = (opts) => ClashSandboxModal.open({ brand: 'pints', ...opts });
  window.openAdultSandbox = (opts) => ClashSandboxModal.open({ brand: 'adult', ...opts });
}
